<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Folder } from 'lucide-vue-next';

import { planReadOnlyReason, useProjectPlanWrite, useProjectWrite } from '@/utils/project-write';
import PlanRelayDialog from '@/components/PlanRelayDialog.vue';
import PlanOwnerAssignmentDialog from '@/components/PlanOwnerAssignmentDialog.vue';
import NodeRescheduleDialog from '@/components/NodeRescheduleDialog.vue';
import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import AppDrawer from '@/components/AppDrawer.vue';
import ExportDataButton from '@/components/ExportDataButton.vue';
import ActivityPlanDashboard from '@/components/ActivityPlanDashboard.vue';
import NodeReviewPanel from '@/components/NodeReviewPanel.vue';
import PageState from '@/components/PageState.vue';
import NodeProgressHistory from '@/components/NodeProgressHistory.vue';
import PeoplePicker from '@/components/PeoplePicker.vue';
import PersonPicker from '@/components/PersonPicker.vue';
import RecordHistory from '@/components/RecordHistory.vue';
import { RouterLink } from 'vue-router';
import StatusChip from '@/components/StatusChip.vue';
import { notify } from '@/services/notice';
import type {
  PlanImportPreview,
  PlanTask,
  Project,
  ProjectPlanResponse,
} from '@/types/domain';
import { buildExportFilename } from '@/utils/csv-export';
import { planExportColumns, planTaskExportRow } from '@/utils/data-export';
import { errorMessage, formatDate, formatResponsibility, statusText } from '@/utils/format';
import { personLabel } from '@/utils/person';
import { confirmAction, promptAction } from '@/services/confirm';
import { useRescheduleAsk } from '@/composables/useRescheduleAsk';
import { dayOf, nodeNeedsRescheduleReason, rescheduleFields } from '@/utils/plan-node-reschedule';
import {
  PLAN_STATUS_LABEL,
  buildProgressIndex,
  isPlanNodeDone,
  linkProgressDraft,
  planStatusOptions,
  progressText,
  progressTitle,
  shanghaiToday,
  type NodeProgress,
  type ProgressDraft,
  type ProgressEdit,
} from '@/utils/plan-progress';
import {
  COMPLETION_NOTE_MIN_CHARS,
  NOTE_REQUIRED_TEXT,
  completionGate,
  completionNoteEnough,
  missingFilesText,
  type CompletionGate,
} from '@/utils/plan-completion';
import { DELAY_CATEGORIES, type DelayCategory } from '@/types/tasks';

interface TreeRow {
  task: PlanTask;
  depth: number;
  hasChildren: boolean;
}

const props = defineProps<{ project: Project }>();
const router = useRouter();
const route = useRoute();
const { access, user } = useSession();
const data = ref<ProjectPlanResponse | null>(null);
const loading = ref(true);
const error = ref('');
const phaseFilter = ref('all');
const expandedIds = ref<string[]>([]);
const selectedIds = ref<Array<string | number>>([]);
const editorOpen = ref(false);
const editorMode = ref<'create' | 'edit'>('edit');
const editorPanel = ref<HTMLElement | null>(null);
const editingId = ref<string | number | null>(null);
const parentNodeId = ref<number | null>(null);

/** 顶层大阶段，新建子阶段时默认挂第一个 */
const topPhases = computed(() =>
  allTasks.value.filter((task) => task.nodeType === 'phase' && !task.parentId));

/**
 * 「所属阶段」下拉的可选项：所有阶段。
 * 编辑时要排掉自己和自己的子孙——挂到自己的下级里会绕成一个圈，树就画不出来了。
 */
const parentOptions = computed(() => {
  const phases = allTasks.value.filter((task) => task.nodeType === 'phase');
  if (editorMode.value !== 'edit' || editingId.value === null) return phases;
  const self = allTasks.value.find((task) => task.id === editingId.value);
  if (!self) return phases;
  /*
    当前挂着的上级不是「阶段」类型时也得在下拉里（0930）：模板里的子阶段在库里记的是 task，
    演示库 206 个活动里 148 个挂在这种子阶段下——下拉里没有它，就显示成一格空白，
    看着像没挂上级，顺手一选就把活动挪走了。
  */
  const originalParentId = editorBase.value?.parentNodeId ?? null;
  const currentParent = allTasks.value.find((task) =>
    originalParentId !== null && Number(task.id) === originalParentId && task.nodeType !== 'phase');
  if (currentParent) phases.unshift(currentParent);
  const banned = new Set<string>([String(self.id)]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const task of allTasks.value) {
      const parentKey = task.parentId === null || task.parentId === undefined ? '' : String(task.parentId);
      const parentNode = allTasks.value.find((item) => String(item.activityId ?? item.id) === parentKey);
      if (parentNode && banned.has(String(parentNode.id)) && !banned.has(String(task.id))) {
        banned.add(String(task.id));
        grew = true;
      }
    }
  }
  return phases.filter((task) => !banned.has(String(task.id)));
});
const editNodeType = ref<'phase' | 'task'>('task');
const editName = ref('');
const editStatus = ref('pending');
const editApplicable = ref(true);
const editReason = ref('');
/** 抽屉里的负责人账号；null = 未分配或保持原有纯文本（见 editOwnerLegacyName） */
const editOwnerId = ref<number | null>(null);
const editOwnerLegacyName = ref('');
/** 协办人（0903，可选多个）：进他们的「我的工作」，负荷仍只算主责 */
const editHelperIds = ref<number[]>([]);
const editStart = ref('');
const editEnd = ref('');
const editActual = ref('');
const editKeyDeliverable = ref(false);
const editReviewRoles = ref('');
/** 完成度（0930 起抽屉里也能报）；null = 没填 */
const editProgress = ref<number | null>(null);
/**
 * 完成说明（0930 Tony：「工作台不能直接点击完成…偷懒直接完成了」）。
 * 没挂交付物的任务标完成时必填，记进改动记录；挂了交付物的看文件，不看这一格。
 */
const editCompletionNote = ref('');
/**
 * 打开抽屉时各格的原值。保存只发**改过的**字段（0930）：
 * 后端按「这一笔带了哪些字段」分权限——只带状态 / 完成度 / 实际完成的，项目组成员就能存；
 * 原来整张表单每格都发，责任人在抽屉里报个进度也会被当成改计划挡掉。
 * 顺带：没动的字段不再原样写回，审计里不会多出一串「改了个寂寞」。
 */
interface EditorSnapshot {
  name: string;
  parentNodeId: number | null;
  status: string;
  applicable: boolean;
  reason: string;
  ownerId: number | null;
  helperIds: number[];
  start: string;
  end: string;
  actual: string;
  progress: number | null;
  keyDeliverable: boolean;
  reviewRoles: string[];
}
const editorBase = ref<EditorSnapshot | null>(null);
/*
  0930 Tony：「阶段工作这边的内容太复杂，还和别的模块的输入内容重复，
  应该只需要开始时间、完成时间等，完成度也可以填写」。
  抽屉正面只留排期和进度；名称、所属阶段、适用性、协办人、评审角色
  收进默认折叠的「更多设置」——60 天 3790 次改动里，名称改过 3 次、所属阶段 5 次、
  评审角色 53 次、不适用原因 0 次，而且适用、需评审在表格行上本来就能点。
  改动记录也收起来，点开才去读。
  「版本说明」同日整个撤掉（Tony：「把版本说明撤掉」）：在跑的节点全库 0 条有值，
  表格那一列、抽屉那一格、导出和 Excel 模板里的那一列都去掉；库里的列和接口字段留着
  （「重新铺设计划」在作废节点上写作废说明用它，没刷新的旧页面也还会带这个字段）。
*/
const moreOpen = ref(false);
const historyOpen = ref(false);
/**
 * 指名评审面板（0916 停发、存量收尾）只在名单上还有人时显示：
 * loading 读名单中 · empty 一行都没有 · rows 有名单 · failed 没读到（照样显示，让错误提示露出来）。
 * 从「去评审」点进来的（focus=review）不等名单，直接显示，免得滚动落空。
 */
const reviewPanelState = ref<'loading' | 'empty' | 'rows' | 'failed'>('loading');
const reviewFocus = ref(false);
/**
 * 抽屉里有没有没保存的改动。程序回填字段（打开抽屉时）不算脏，
 * 只有人真正敲过之后，关闭才需要确认——打开看一眼就关不该被打扰。
 */
const editorDirty = ref(false);
let fillingEditor = false;
watch(
  [editName, editStatus, editApplicable, editReason, editOwnerId, editHelperIds, parentNodeId,
    editStart, editEnd, editActual, editProgress, editKeyDeliverable, editReviewRoles, editCompletionNote],
  () => { if (!fillingEditor && editorOpen.value) editorDirty.value = true; },
  { deep: true },
);
const saving = ref(false);
const bulkOpen = ref(false);
const bulkStatus = ref('');
const bulkApplicable = ref('');
const bulkResponsibility = ref('');
const bulkStart = ref('');
const bulkEnd = ref('');
const bulkSaving = ref(false);
const importInput = ref<HTMLInputElement | null>(null);
const importLoading = ref(false);
const importConfirming = ref(false);
const importPreview = ref<PlanImportPreview | null>(null);
/*
  每个节点的实际交付物汇总（P11 第 1 步，试用反馈 0819：
  「任务项已交付，在阶段工作模块…未见到相关交付物」）。
  任务看板能看到交付物名，这页却一点痕迹没有——数据本来就有（交付物挂 nodeId），
  这里取一次全量按节点聚合，行上显示「2/3 已交」并可跳到交付物页按节点过滤。
*/
/**
 * 取消了的交付物不算（0930）：项目经理把不需要的取消掉，是「标完成要有证据」的正规出口，
 * 这里要是还算着，行上就永远是「1/2 已交」。missing = 没取消、还没传文件的标题。
 */
interface NodeDeliverableStat { total: number; withDoc: number; approved: number; missing: string[] }
const deliverableStats = ref<Record<string, NodeDeliverableStat>>({});
async function loadDeliverableStats(): Promise<void> {
  try {
    const items = await eqsApi.projectDeliverables(props.project.id);
    const map: Record<string, NodeDeliverableStat> = {};
    for (const item of items) {
      if (item.nodeId === null || item.nodeId === undefined) continue;
      if (String(item.status) === 'cancelled') continue;
      const key = String(item.nodeId);
      const stat = map[key] ?? (map[key] = { total: 0, withDoc: 0, approved: 0, missing: [] });
      stat.total += 1;
      if (item.documentId) stat.withDoc += 1;
      else stat.missing.push(item.title);
      if (String(item.approvalStatus) === 'approved' || String(item.status) === 'approved') stat.approved += 1;
    }
    deliverableStats.value = map;
  } catch {
    // 统计取不到不拦页面：这一列显示为空即可
    deliverableStats.value = {};
  }
}
function deliverableStatOf(
  task: { id: number | string; nodeType?: string; activityId?: string | number | null },
): NodeDeliverableStat | null {
  const own = deliverableStats.value[String(task.id)] ?? null;
  // 阶段行给子任务合计：默认视图是折叠的，只显示阶段行——
  // 不聚合的话，折叠态下这条统计根本没机会出现
  if (task.nodeType !== 'phase') return own;
  const sum: NodeDeliverableStat = {
    total: own?.total ?? 0, withDoc: own?.withDoc ?? 0, approved: own?.approved ?? 0, missing: [...(own?.missing ?? [])],
  };
  for (const child of allTasks.value) {
    if (String(child.parentId ?? '') !== String(task.activityId ?? '')) continue;
    const stat = deliverableStats.value[String(child.id)];
    if (!stat) continue;
    sum.total += stat.total; sum.withDoc += stat.withDoc; sum.approved += stat.approved;
    sum.missing.push(...stat.missing);
  }
  return sum.total ? sum : null;
}

/** 这个任务要标完成还差什么（判据镜像后端 plan-node-completion-gate.ts，见 utils/plan-completion） */
function completionGateOf(task: PlanTask): CompletionGate | null {
  const own = deliverableStats.value[String(task.id)];
  return completionGate(task.nodeType, own ? { live: own.total, missing: own.missing } : null);
}

/**
 * 表格格子里把任务改成「做完」之前问一句（完成度填 100、填实际完成）。
 * 缺文件：直接说缺哪几份，不改；没挂交付物：弹框要一句完成说明。
 * 返回 null = 不改了（格子退回原值）；返回字符串 = 带上的完成说明（挂了交付物的是空串）。
 */
async function completionEvidenceForCell(task: PlanTask): Promise<string | null> {
  let gate = completionGateOf(task);
  if (gate?.kind === 'files') {
    // 统计是打开页面时取的，人可能刚在别的页签传完文件——挡之前先重取一次
    await loadDeliverableStats();
    gate = completionGateOf(task);
  }
  if (!gate || gate.kind === 'ok') return '';
  if (gate.kind === 'files') {
    notify('error', `「${task.name}」还不能标完成`, missingFilesText(gate.missing));
    return null;
  }
  const note = await promptAction({
    title: `标完成「${task.name}」`,
    message: '这条任务没挂交付物。写一句做了什么、结果放在哪，会记进这条任务的改动记录。',
    prompt: '完成说明',
    promptPlaceholder: '例如：评审会已开，纪要放在项目文件夹 P03',
    promptRequired: true,
    confirmLabel: '标完成',
  });
  if (note === null) return null;
  if (!completionNoteEnough(note)) {
    notify('error', `「${task.name}」没有标完成`, NOTE_REQUIRED_TEXT);
    return null;
  }
  return note.trim();
}
/*
  状态显示口径和任务看板保持一致（0820 第 6 条）：
  交付物提交进审批流还没批 → 待评审；没动过 → 未开始。
  阶段工作这张表原来只显示 status_key 的原值，「待评审」根本没有位置。
*/
function planAwaitingReview(task: PlanTask): boolean {
  return task.approvalStatus === 'pending'
    && !['completed', 'approved', 'not_applicable', 'cancelled'].includes(task.status ?? '')
    && !task.actualEnd;
}

function planChipValue(task: PlanTask): string {
  if (planAwaitingReview(task)) return task.overdue ? 'overdue' : 'in_review';
  return task.overdue ? 'overdue' : task.status;
}

/** 节点状态的中文名；只读抽屉和别处都读这一份，不各写各的 */
const NODE_STATUS_LABEL: Readonly<Record<string, string>> = {
  pending: '待处理', in_progress: '进行中', blocked: '阻塞', completed: '已完成',
  approved: '已批准', rejected: '已退回', not_applicable: '不适用',
};

function planStatusLabel(task: PlanTask): string | undefined {
  if (planAwaitingReview(task)) return task.overdue ? '待评审（已超期）' : '待评审';
  if (task.overdue) return undefined;
  if (!task.actualEnd && !task.progress && ['pending', 'open', ''].includes(task.status ?? '')) return '未开始';
  return undefined;
}

function deliverableLink(task: { id: number | string }): string {
  return `/projects/${props.project.id}/deliverables?nodeId=${task.id}`;
}

const people = ref<Array<{ id: number; displayName: string; orgName: string | null }>>([]);

/**
 * 行内编辑的每格状态。
 *
 * 排一个项目的期要动上百个日期，原来每改一格都要「开抽屉 → 填 → 确认弹窗 → 整页重载」，
 * 旧 EQS 是直接点单元格改。这里做成改完即存：本地先落，请求失败再回滚，
 * 键是 `节点ID:字段`，只让那一格给反馈，不动整张表。
 */
const cellState = ref<Record<string, 'saving' | 'saved' | 'error'>>({});

/*
  改计划完成日要写原因（0926 Tony）。判据在 utils/plan-node-reschedule.ts 和后端同一套：
  已排期、还开着的活动改了完成日（含清空）才问；补排期、了结后的记账不问。
  弹窗状态用 useRescheduleAsk，取消就什么都不发。
*/
const reschedule = useRescheduleAsk();
const bulkReasonCategory = ref<DelayCategory>('其他');
const bulkReason = ref('');

function cellKey(task: PlanTask, field: string): string {
  return `${String(task.id)}:${field}`;
}

function cellStatus(task: PlanTask, field: string): string {
  return cellState.value[cellKey(task, field)] ?? '';
}

function markCell(task: PlanTask, field: string, state: 'saving' | 'saved' | 'error' | null): void {
  const key = cellKey(task, field);
  const next = { ...cellState.value };
  if (state === null) delete next[key];
  else next[key] = state;
  cellState.value = next;
}

async function saveCell(
  task: PlanTask,
  field: string,
  payload: Record<string, unknown>,
  applyLocal: () => void,
  revertLocal: () => void,
): Promise<void> {
  applyLocal();
  markCell(task, field, 'saving');
  try {
    await eqsApi.updatePlanNode(props.project.id, task.id, payload);
    markCell(task, field, 'saved');
    window.setTimeout(() => {
      if (cellStatus(task, field) === 'saved') markCell(task, field, null);
    }, 1600);
  } catch (caught) {
    // 后端拒绝了（比如开始晚于完成、上级不适用），把格子改回原值再说
    revertLocal();
    markCell(task, field, 'error');
    notify('error', `“${task.name}”未保存`, errorMessage(caught));
  }
}

const DATE_FIELDS = {
  planStart: 'plannedStartAt',
  planEnd: 'plannedEndAt',
  actualEnd: 'actualAt',
} as const;

/**
 * 计划日期按东八区的整天写，保留 +08:00 而不是转成 UTC。
 *
 * 转成 UTC 的话「2026-08-03 零点」会变成 `2026-08-02T16:00:00Z`，
 * 而表格里显示的是 `planStart.slice(0, 10)`——选了 8 月 3 号，格子里立刻变成 8 月 2 号。
 * 接口返回的也是带 +08 的本地时间（库的 timezone 就是 Asia/Shanghai），
 * 用同一种写法，本地先落的值和刷新后从接口拿到的值才对得上。
 */
function shanghaiDate(value: string, endOfDay = false): string | null {
  return value ? `${value}T${endOfDay ? '23:59:59' : '00:00:00'}+08:00` : null;
}

async function onDateChange(
  task: PlanTask,
  field: keyof typeof DATE_FIELDS,
  event: Event,
): Promise<void> {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const previous = task[field];
  // 计划开始取当天零点，计划完成/实际完成取当天结束
  const next = shanghaiDate(value, field !== 'planStart');
  if (next === previous) return;
  let extra: Record<string, string> = {};
  // 填实际完成 = 标完成（后端会顺手把状态改成完成）：先过「完成要有证据」（0930）
  if (field === 'actualEnd' && next !== null && !isPlanNodeDone(task)) {
    const note = await completionEvidenceForCell(task);
    if (note === null) {
      input.value = previous?.slice(0, 10) || '';
      return;
    }
    if (note) extra = { completionNote: note };
  }
  if (field === 'planEnd' && nodeNeedsRescheduleReason(task, value || null)) {
    const answer = await reschedule.ask({ title: task.name, fromDay: dayOf(task.planEnd), toDay: value || null });
    if (!answer) {
      // 不写原因就不改：格子退回原值（数据没动过，得手工把输入框拨回去）
      input.value = previous?.slice(0, 10) || '';
      return;
    }
    extra = rescheduleFields(answer, 'single');
  }
  void saveCell(
    task,
    field,
    { [DATE_FIELDS[field]]: next, ...extra },
    () => { task[field] = next; },
    () => { task[field] = previous; },
  );
}

function onOwnerPicked(task: PlanTask, userId: number | null): void {
  const previousId = task.ownerUserId ?? null;
  const previousName = task.ownerName;
  if (userId === null) {
    // 只解绑账号，不动 responsibility 文本——那是模板的参考角色，
    // 试用反馈：清除负责人时参考角色跟着消失
    // 人清空过的，团队怎么变都不再自动落（后端记 manual，这里同步改掉「按团队角色」那个小标）
    const previousSource = task.ownerSource ?? null;
    void saveCell(
      task,
      'owner',
      { ownerUserId: null },
      () => { task.ownerUserId = null; task.ownerName = null; task.ownerSource = 'manual'; },
      () => { task.ownerUserId = previousId; task.ownerName = previousName; task.ownerSource = previousSource; },
    );
    return;
  }
  const person = people.value.find((item) => item.id === userId);
  if (!person) return;
  const previousSource = task.ownerSource ?? null;
  void saveCell(
    task,
    'owner',
    { ownerUserId: person.id },
    () => {
      task.ownerUserId = person.id;
      task.ownerName = person.displayName;
      // 选的是同一个人不算改（后端也这么判），别的都算人定的
      if (person.id !== previousId) task.ownerSource = 'manual';
    },
    () => { task.ownerUserId = previousId; task.ownerName = previousName; task.ownerSource = previousSource; },
  );
}

function onOwnerUnresolved(text: string): void {
  notify('error', '没找到这个人', `系统里没有唯一叫「${text}」的账号，负责人保持原样。从下拉建议里选一个。`);
}

async function onApplicableToggle(task: PlanTask): Promise<void> {
  const next = task.applicable === false;
  // 阶段一关，下面整条支线都不能启用，这个值得问一句；单个任务点错了再点回来就行
  if (task.nodeType === 'phase' && !next
    && !await confirmAction({ title: `确认把阶段“${task.name}”整体设为不适用？`, danger: true })) return;
  const previous = task.applicable;
  const previousStatus = task.status;
  void saveCell(
    task,
    'applicable',
    { applicable: next },
    () => {
      task.applicable = next;
      // 后端会把不适用的节点状态改成 not_applicable，本地跟上免得状态列对不上
      if (!next) task.status = 'not_applicable';
      else if (task.status === 'not_applicable') task.status = 'pending';
    },
    () => { task.applicable = previous; task.status = previousStatus; },
  );
}

async function onProgressChange(task: PlanTask, event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const raw = input.value.trim();
  const previous = { progress: task.progress, status: task.status, actualEnd: task.actualEnd };
  if (raw === '') {
    void saveCell(task, 'progress', { progress: null },
      () => { task.progress = null; }, () => { task.progress = previous.progress; });
    return;
  }
  const next = Math.round(Number(raw));
  // 后端会挡住越界，但先在这里拦一下，省一次失败往返
  if (!Number.isFinite(next) || next < 0 || next > 100) {
    markCell(task, 'progress', 'error');
    notify('error', '完成度无效', '请填 0 到 100 之间的整数。');
    return;
  }
  if (next === previous.progress) return;
  /*
    和抽屉同一套联动（utils/plan-progress 的 linkProgressDraft）：填 100 = 做完了，
    状态跟着改完成、实际完成记今天；待处理的报了进度 = 进行中。
    不然就是「100% 却还挂着逾期」，甘特上一条满格的红条，谁看都糊涂。
    已完成的节点这一格是只读的，往回改走抽屉（那边看得见会重新打开它）。
  */
  const draft: ProgressDraft = {
    progress: task.progress ?? null,
    actual: task.actualEnd?.slice(0, 10) ?? '',
    status: task.status ?? '',
  };
  const linked = linkProgressDraft(draft, { field: 'progress', value: next }, draft, shanghaiToday());
  const payload: Record<string, unknown> = { progress: linked.progress };
  if (linked.status !== draft.status) payload.status = linked.status;
  if (linked.actual !== draft.actual) payload.actualAt = shanghaiDate(linked.actual, true);
  // 填到 100 = 标完成：先过「完成要有证据」（0930）；不过就把格子拨回去
  if (!isPlanNodeDone(task) && isPlanNodeDone({ status: linked.status, actualEnd: linked.actual || null })) {
    const note = await completionEvidenceForCell(task);
    if (note === null) {
      input.value = previous.progress === null || previous.progress === undefined ? '' : String(previous.progress);
      return;
    }
    if (note) payload.completionNote = note;
  }
  void saveCell(task, 'progress', payload,
    () => {
      task.progress = linked.progress;
      task.status = linked.status;
      if ('actualAt' in payload) task.actualEnd = payload.actualAt as string | null;
    },
    () => {
      task.progress = previous.progress;
      task.status = previous.status;
      task.actualEnd = previous.actualEnd;
    });
}

/* ---------- 节点资料文件夹 / 附件 / 催办 ---------- */

const nodeFolders = ref<Map<string, { documentId: number; title: string | null; fileCount: number; source?: 'linked' | 'deliverable' }>>(new Map());
const reminding = ref<string | number | null>(null);

async function loadFolders(): Promise<void> {
  try {
    const payload = await eqsApi.planNodeFolders(props.project.id);
    const list = (payload.folders ?? []) as Array<{
      nodeId: number; documentId: number; title: string | null; fileCount: number;
      source?: 'linked' | 'deliverable';
    }>;
    nodeFolders.value = new Map(list.map((item) => [String(item.nodeId), item]));
  } catch {
    // 取不到映射只是少一个跳转入口，不该影响整张计划表
    nodeFolders.value = new Map();
  }
}

function folderFor(task: PlanTask) {
  return nodeFolders.value.get(String(task.id)) ?? null;
}

function openFolder(task: PlanTask): void {
  const folder = folderFor(task);
  if (!folder) return;
  void router.push({ name: 'project-files', params: { projectId: String(props.project.id) },
    query: { documentId: String(folder.documentId) } });
}

function downloadAttachments(task?: PlanTask): void {
  window.open(eqsApi.planAttachmentsArchiveUrl(props.project.id, task?.id), '_blank');
}

async function remind(task: PlanTask): Promise<void> {
  const note = await promptAction({
    title: `催办「${task.name}」`,
    message: '会给责任人发一条待办提醒；同一天对同一节点只发一次。',
    prompt: '留言（可留空）',
    promptPlaceholder: '例如：客户周五要，请优先处理',
    confirmLabel: '发送催办',
  });
  if (note === null) return;
  reminding.value = task.id;
  try {
    const result = await eqsApi.remindPlanNode(props.project.id, task.id, note || undefined) as {
      notified: number; ownerName: string | null; skipped: string | null;
    };
    if (result.notified) notify('success', '催办已发出', `${result.ownerName} 会看到一条待办提醒。`);
    else notify('error', '催办没有发出', result.skipped ?? '未知原因');
  } catch (caught) {
    notify('error', '催办失败', errorMessage(caught));
  } finally {
    reminding.value = null;
  }
}

function onReviewToggle(task: PlanTask): void {
  const next = !task.keyDeliverable;
  const previous = task.keyDeliverable;
  void saveCell(
    task,
    'review',
    { keyDeliverable: next },
    () => { task.keyDeliverable = next; },
    () => { task.keyDeliverable = previous; },
  );
}

/*
  能不能改 = 账号不是只读 且 这个项目在自己名下。
  所有项目都能看（Tony 2026-08-18 定的口径），收着的只有写。
*/
/*
  0914 Tony 第 12 条：阶段工作只给项目经理（管理者）改。
  比项目的一般写权限窄一档——判据在 useProjectPlanWrite 一处，
  后端 guardPlanWrite 用的是同一条，界面不自己再判一遍。
*/
const canWrite = useProjectPlanWrite(computed(() => props.project));
/*
  **报自己的进度 ≠ 改计划**（0915 回归修复）。

  第 12 条收的是排期（「排期一动，下游节点、Gate 和交付物到期日都会跟着变」）。
  但我一开始把整页都按项目经理灰掉了，连带把三件本来就该让责任人做的事也挡了：
  行上的「实际完成 / 完成度」、从「我的工作」点进来的节点抽屉、抽屉里的评审面板。
  销售点「完成」吃 403、评审人点「去评审」打不开抽屉，都是这么来的。

  所以两个权限分开：canWrite = 改计划（项目经理）；canReport = 报进度和评审（项目组成员）。
  后端按**这一笔改了哪些字段**判，和这里是同一条口径。
*/
const canReport = useProjectWrite(computed(() => props.project));
const planReadOnly = computed(() =>
  planReadOnlyReason(props.project, access.value?.canWrite !== false));

/*
  「这个节点是不是可以标完成了」。

  全公司计划节点的完成填报率只有 1.9%（4160 个填了 79 个）——控制台上每个项目
  都写着 0 个已确认，不是没干活，是没人回来填。方向是 Tony 8/19 定的：
  **系统提醒，人确认**，一条都不自动标。判据和覆盖率见后端 node-completion-advisor.ts。
*/
interface CompletionSuggestion {
  nodeId: number; nodeName: string; phase: string | null;
  ownerName: string | null; suggestedDate: string; reasons: string[];
}
const completion = ref<{ suggestions: CompletionSuggestion[]; pendingNodes: number } | null>(null);
const completionOpen = ref(false);
const completionBusy = ref(false);
const completionDone = ref<Set<number>>(new Set());
const openCompletion = computed(() =>
  (completion.value?.suggestions ?? []).filter((item) => !completionDone.value.has(item.nodeId)));

async function askCompletion(): Promise<void> {
  completionBusy.value = true;
  completionOpen.value = true;
  try {
    completion.value = await eqsApi.adviseCompletion(props.project.id) as unknown as
      NonNullable<typeof completion.value>;
    completionDone.value = new Set();
    if (!completion.value!.suggestions.length) {
      notify('success', '没有可提示的节点',
        `${completion.value!.pendingNodes} 个节点还没填完成日期，但都找不到「已经做完」的痕迹。`);
    }
  } catch (caught) {
    notify('error', '算不出建议', errorMessage(caught));
  } finally {
    completionBusy.value = false;
  }
}

async function confirmCompletion(items: CompletionSuggestion[]): Promise<void> {
  if (!items.length || completionBusy.value) return;
  completionBusy.value = true;
  try {
    const result = await eqsApi.confirmCompletion(props.project.id,
      items.map((item) => ({ nodeId: item.nodeId, date: item.suggestedDate }))) as {
        done?: number; failed?: Array<{ nodeId: number; why: string }>;
      };
    for (const item of items) completionDone.value = new Set(completionDone.value).add(item.nodeId);
    notify(result.failed?.length ? 'error' : 'success',
      `确认完成 ${result.done ?? 0} 个节点`,
      result.failed?.length ? `${result.failed.length} 个没写上：${result.failed[0]!.why}` : '');
    await load();
  } catch (caught) {
    notify('error', '没写上', errorMessage(caught));
  } finally {
    completionBusy.value = false;
  }
}
const allTasks = computed(() => data.value?.tasks ?? []);
/** 抽屉正在编辑的那条（删除入口要判断它是不是手工建的） */
const editingTask = computed(() =>
  allTasks.value.find((task) => task.id === editingId.value) ?? null);

/** 完成度只有一个算法（utils/plan-progress）：表格、抽屉、计划甘特读的是同一份 */
const progressIndex = computed(() => buildProgressIndex(allTasks.value));
const NO_PROGRESS: NodeProgress = { value: null, source: 'none' };
function progressOf(task: PlanTask | null | undefined): NodeProgress {
  return task ? progressIndex.value.get(String(task.id)) ?? NO_PROGRESS : NO_PROGRESS;
}
/**
 * 表格里这一格能不能直接改：阶段按下面的活动汇总（改下面的）；
 * 做完的就是 100（要重新打开走抽屉，那边看得见状态和实际完成跟着变）；不适用的不算。
 */
function progressEditableInline(task: PlanTask): boolean {
  return canReport.value
    && task.applicable !== false
    && !isPlanNodeDone(task)
    && progressOf(task).source !== 'rollup';
}

/* ---------- 抽屉里的完成度 · 实际完成 · 状态 ---------- */

/** 抽屉里的完成度是按下级汇总的（阶段），不是人填的 */
const editIsRollup = computed(() =>
  editorMode.value === 'edit' && progressOf(editingTask.value).source === 'rollup');
/** 状态下拉：当前 + 能一步转过去的（批准 / 退回走审批链，不适用在「更多设置」里） */
const editStatusOptions = computed(() => planStatusOptions(editorBase.value?.status ?? editStatus.value));

function applyProgressEdit(edit: ProgressEdit): void {
  const base = editorBase.value;
  const baseDraft: ProgressDraft = base
    ? { progress: base.progress, actual: base.actual, status: base.status }
    : { progress: null, actual: '', status: '' };
  const next = linkProgressDraft(
    { progress: editProgress.value, actual: editActual.value, status: editStatus.value },
    edit,
    baseDraft,
    shanghaiToday(),
  );
  editProgress.value = next.progress;
  editActual.value = next.actual;
  editStatus.value = next.status;
}

function onDraftProgressText(event: Event): void {
  const input = event.target as HTMLInputElement;
  const raw = input.value.trim();
  if (raw === '') {
    applyProgressEdit({ field: 'progress', value: null });
    return;
  }
  const value = Math.round(Number(raw));
  if (!Number.isFinite(value) || value < 0 || value > 100) {
    notify('error', '完成度无效', '请填 0 到 100 之间的整数。');
    input.value = editProgress.value === null ? '' : String(editProgress.value);
    return;
  }
  applyProgressEdit({ field: 'progress', value });
}

/** 这一笔会把任务从「没做完」变成「做完」——要过「完成要有证据」那道门（0930） */
const editBecomingDone = computed(() => {
  const base = editorBase.value;
  if (!base || editorMode.value !== 'edit') return false;
  return !isPlanNodeDone({ status: base.status, actualEnd: base.actual || null })
    && isPlanNodeDone({ status: editStatus.value, actualEnd: editActual.value || null });
});
/** 抽屉里「标完成还差什么」；不往完成走、或者是阶段，就是 null */
const editCompletionGate = computed<CompletionGate | null>(() =>
  editBecomingDone.value && editingTask.value ? completionGateOf(editingTask.value) : null);
const completionNoteInput = ref<HTMLTextAreaElement | null>(null);

/** 联动改了别的格子时说一句，别让人以为是自己手滑；判「做完」和 isPlanNodeDone 同一条 */
const progressLinkHint = computed(() => {
  const base = editorBase.value;
  if (!base || editorMode.value !== 'edit') return '';
  const baseDone = isPlanNodeDone({ status: base.status, actualEnd: base.actual || null });
  const draftDone = isPlanNodeDone({ status: editStatus.value, actualEnd: editActual.value || null });
  if (baseDone && !draftDone) {
    return '这条原来已完成，现在会重新打开：状态回到进行中、实际完成清掉。';
  }
  if (!baseDone && draftDone && editActual.value) {
    return `保存后算做完：实际完成记为 ${editActual.value}，日期不对可以改。`;
  }
  return '';
});
const activityMap = computed(() => new Map(
  allTasks.value
    .filter((task) => task.activityId !== null && task.activityId !== undefined)
    .map((task) => [String(task.activityId), task]),
));
const phaseRoots = computed(() => allTasks.value.filter((task) =>
  (task.nodeType === 'phase' || /phase|stage|阶段/i.test(task.phaseCode))
  && (task.parentId === null || String(task.parentId) === '0')));
const phaseOptions = computed(() => phaseRoots.value.map((task) => ({
  value: String(task.activityId ?? task.id),
  label: task.name,
})));

function taskKey(task: PlanTask): string {
  return String(task.activityId ?? task.id);
}

function phaseFor(task: PlanTask): string | null {
  const seen = new Set<string>();
  let current: PlanTask | undefined = task;
  while (current) {
    if (
      (current.nodeType === 'phase' || /phase|stage|阶段/i.test(current.phaseCode))
      && (current.parentId === null || String(current.parentId) === '0')
    ) {
      return taskKey(current);
    }
    const parent = current.parentId === null ? '' : String(current.parentId);
    if (!parent || parent === '0' || seen.has(parent)) break;
    seen.add(parent);
    current = activityMap.value.get(parent);
  }
  return null;
}

/** 看板点下去的筛选；与阶段筛选叠加 */
const gapFilter = ref<{ kind: string; value: string } | null>(null);

/**
 * 通知点进来的筛选：只看我的、只看逾期/临期。
 *
 * 通知从「一个任务一条」改成「一个项目一条摘要」之后，点进来必须落到
 * 筛好的那几行，否则人还得在几百行计划里自己找（2026-08-13）。
 */
const noticeFilter = ref<{ mine: boolean; kind: 'overdue' | 'duesoon' | null }>({ mine: false, kind: null });

const noticeFilterLabel = computed(() => {
  if (!noticeFilter.value.kind && !noticeFilter.value.mine) return '';
  const parts = [];
  if (noticeFilter.value.mine) parts.push('我负责的');
  if (noticeFilter.value.kind === 'overdue') parts.push('已逾期');
  if (noticeFilter.value.kind === 'duesoon') parts.push('3 天内到期');
  return parts.join(' · ');
});

function clearNoticeFilter(): void {
  noticeFilter.value = { mine: false, kind: null };
}

/** 从通知链接读筛选条件 */
function applyNoticeFilterFromQuery(): void {
  const single = (key: string): string =>
    String(Array.isArray(route.query[key]) ? route.query[key][0] : route.query[key] ?? '');
  const mine = single('mine') === '1';
  const kind = single('overdue') === '1' ? 'overdue' : single('duesoon') === '1' ? 'duesoon' : null;
  noticeFilter.value = { mine, kind };
  // 带筛选进来时不要再被阶段筛选二次收窄，否则可能一行都不剩
  if (mine || kind) phaseFilter.value = 'all';
}

function matchesNoticeFilter(task: PlanTask): boolean {
  const filter = noticeFilter.value;
  if (!filter.mine && !filter.kind) return true;
  if (task.applicable === false) return false;
  const done = TERMINAL_STATUSES.has(task.status) || Boolean(task.actualEnd);
  if (filter.mine) {
    const myId = user.value?.id ?? null;
    if (myId === null || Number(task.ownerUserId ?? 0) !== myId) return false;
  }
  if (filter.kind === 'overdue' && !(task.overdue && !done)) return false;
  if (filter.kind === 'duesoon') {
    if (done || !task.planEnd) return false;
    const end = new Date(task.planEnd).getTime();
    const now = Date.now();
    if (!(end >= now && end <= now + 3 * 24 * 60 * 60 * 1000)) return false;
  }
  return true;
}

const TERMINAL_STATUSES = new Set(['completed', 'approved', 'cancelled', 'not_applicable']);

function matchesGap(task: PlanTask): boolean {
  const filter = gapFilter.value;
  if (!filter) return true;
  if (filter.kind === 'status') return (task.status || 'pending') === filter.value;
  // 缺口只看「适用」的节点，与看板口径一致
  if (task.applicable === false) return false;
  const done = TERMINAL_STATUSES.has(task.status) || Boolean(task.actualEnd);
  if (filter.value === 'no-dates') return !task.planStart || !task.planEnd;
  if (filter.value === 'no-owner') return !(task.ownerUserId || task.ownerName || task.ownerRole);
  if (filter.value === 'overdue') return task.overdue && !done;
  if (filter.value === 'awaiting-approval') {
    return task.keyDeliverable && task.approvalRequired && task.approvalStatus !== 'approved';
  }
  return true;
}

const visibleTasks = computed(() => {
  const inPhase = phaseFilter.value === 'all'
    ? allTasks.value
    : allTasks.value.filter((task) => phaseFor(task) === phaseFilter.value);
  return inPhase.filter(matchesGap).filter(matchesNoticeFilter);
});
const exportRows = computed(() => visibleTasks.value.map((task) => {
  const phaseKey = phaseFor(task);
  const phaseName = phaseRoots.value.find((phase) => taskKey(phase) === phaseKey)?.name || '未归入阶段';
  return planTaskExportRow(props.project, task, phaseName, statusText(task.status), progressOf(task).value);
}));
const exportFilename = computed(() =>
  buildExportFilename([props.project.projectNo, '阶段工作内容']));

const treeRows = computed<TreeRow[]>(() => {
  const allowed = new Set(visibleTasks.value.map((task) => String(task.id)));
  const children = new Map<string, PlanTask[]>();
  const roots: PlanTask[] = [];
  for (const task of visibleTasks.value) {
    const parent = task.parentId === null ? '0' : String(task.parentId);
    if (parent === '0' || !activityMap.value.has(parent) || !allowed.has(String(activityMap.value.get(parent)?.id))) {
      roots.push(task);
    } else {
      const list = children.get(parent) ?? [];
      list.push(task);
      children.set(parent, list);
    }
  }
  const expanded = new Set(expandedIds.value);
  const rows: TreeRow[] = [];
  const walk = (items: PlanTask[], depth: number) => {
    for (const task of items) {
      const nested = children.get(taskKey(task)) ?? [];
      rows.push({ task, depth, hasChildren: nested.length > 0 });
      if (nested.length && expanded.has(taskKey(task))) walk(nested, depth + 1);
    }
  };
  walk(roots, 0);
  return rows;
});

watch(phaseFilter, () => {
  selectedIds.value = [];
  expandedIds.value = phaseFilter.value === 'all' ? [] : [phaseFilter.value];
});

function isExpanded(task: PlanTask): boolean {
  return expandedIds.value.includes(taskKey(task));
}

function toggleExpanded(task: PlanTask): void {
  const key = taskKey(task);
  expandedIds.value = isExpanded(task)
    ? expandedIds.value.filter((item) => item !== key)
    : [...expandedIds.value, key];
}

function expandAll(): void {
  expandedIds.value = visibleTasks.value.map(taskKey);
}

function collapseAll(): void {
  expandedIds.value = [];
}

function rowSelected(task: PlanTask): boolean {
  return selectedIds.value.some((id) => String(id) === String(task.id));
}

function toggleSelection(task: PlanTask): void {
  selectedIds.value = rowSelected(task)
    ? selectedIds.value.filter((id) => String(id) !== String(task.id))
    : [...selectedIds.value, task.id];
}

function toggleAllVisible(): void {
  const ids = treeRows.value.map(({ task }) => task.id);
  const allSelected = ids.length > 0 && ids.every((id) =>
    selectedIds.value.some((selected) => String(selected) === String(id)));
  selectedIds.value = allSelected ? [] : ids;
}

function isoDate(value: string, endOfDay = false): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}+08:00`).toISOString();
}

/** 新建时光标落在名称上；编辑时不抢焦点——抽屉正面第一格是日期，人多半是来报进度的 */
async function focusEditor(): Promise<void> {
  await nextTick();
  if (editorMode.value !== 'create') return;
  editorPanel.value?.querySelector<HTMLInputElement>('input[aria-label="活动名称"]')?.focus();
}

function resetEditor(): void {
  editName.value = '';
  editStatus.value = 'pending';
  editApplicable.value = true;
  editReason.value = '';
  editOwnerId.value = null;
  editOwnerLegacyName.value = '';
  editHelperIds.value = [];
  editStart.value = '';
  editEnd.value = '';
  editActual.value = '';
  editProgress.value = null;
  editKeyDeliverable.value = false;
  editReviewRoles.value = '';
  editorBase.value = null;
  moreOpen.value = false;
  historyOpen.value = false;
}

function splitRoles(text: string): string[] {
  return text.split(/[、,，;；]/u).map((item) => item.trim()).filter(Boolean);
}

function sortedIds(ids: readonly number[], ownerId: number | null): number[] {
  return ids.filter((id) => id !== ownerId).sort((a, b) => a - b);
}

function sameList(left: readonly unknown[], right: readonly unknown[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

/** 当前表单的值，打开抽屉时记一份当原值，保存时拿来比 */
function snapshotEditor(): EditorSnapshot {
  return {
    name: editName.value.trim(),
    parentNodeId: parentNodeId.value,
    status: editStatus.value,
    applicable: editApplicable.value,
    reason: editReason.value.trim(),
    ownerId: editOwnerId.value,
    helperIds: sortedIds(editHelperIds.value, editOwnerId.value),
    start: editStart.value,
    end: editEnd.value,
    actual: editActual.value,
    progress: editProgress.value,
    keyDeliverable: editKeyDeliverable.value,
    reviewRoles: splitRoles(editReviewRoles.value),
  };
}

/**
 * 新建。
 *
 * 不带 parent 时建的是**子阶段**，不是顶层大阶段——P01～P06 是模板给的，
 * 基本不需要再加；真正要加的是某个大阶段下面的子阶段（Tony 2026-08-18）。
 * 抽屉里的「所属阶段」可以改挂到哪个大阶段下，留空才落到顶层。
 */
async function openCreate(parent?: PlanTask, kind: 'phase' | 'task' = 'phase'): Promise<void> {
  fillingEditor = true;
  resetEditor();
  editorMode.value = 'create';
  editingId.value = null;
  // 没指定父级时默认挂在第一个大阶段下，省得每次都要先想「放哪」
  parentNodeId.value = parent
    ? Number(parent.id)
    : (topPhases.value[0] ? Number(topPhases.value[0].id) : null);
  editNodeType.value = parent ? kind : 'phase';
  editorOpen.value = true;
  await nextTick();
  fillingEditor = false;
  editorDirty.value = false;
  await focusEditor();
}

/**
 * 行点击。
 *
 * 复选框、链接、按钮这些自己有行为的元素点了不该顺带打开抽屉，
 * 否则勾选一条就跳出编辑面板。
 */
function openEditFromRow(event: MouseEvent, task: PlanTask): void {
  const target = event.target as HTMLElement | null;
  if (target?.closest('input, button, a, select, textarea, label')) return;
  void openEdit(task);
}

async function openEdit(task: PlanTask): Promise<void> {
  fillingEditor = true;
  editorMode.value = 'edit';
  editingId.value = task.id;
  // 带出当前挂在谁下面，才能在抽屉里改归属
  parentNodeId.value = task.parentId === null || task.parentId === undefined
    ? null
    : (allTasks.value.find((item) => String(item.activityId ?? item.id) === String(task.parentId))?.id as number | undefined) ?? null;
  editNodeType.value = task.nodeType === 'phase' ? 'phase' : 'task';
  editName.value = task.name;
  editStatus.value = task.status || 'pending';
  editApplicable.value = task.applicable !== false;
  editReason.value = task.applicabilityReason || '';
  editOwnerId.value = task.ownerUserId ?? null;
  editOwnerLegacyName.value = task.ownerUserId ? '' : (task.ownerName || '');
  editHelperIds.value = (task.helpers ?? []).map((helper) => helper.id);
  editStart.value = task.planStart?.slice(0, 10) || '';
  editEnd.value = task.planEnd?.slice(0, 10) || '';
  editActual.value = task.actualEnd?.slice(0, 10) || '';
  // 做完的就是 100：迁移来的节点常常只有实际完成、完成度是空的
  editProgress.value = isPlanNodeDone(task) ? 100 : (task.progress ?? null);
  editKeyDeliverable.value = task.keyDeliverable;
  editReviewRoles.value = (task.reviewRoles ?? []).join('；');
  editCompletionNote.value = '';
  editorBase.value = snapshotEditor();
  moreOpen.value = false;
  historyOpen.value = false;
  reviewPanelState.value = 'loading';
  reviewFocus.value = false;
  editorOpen.value = true;
  await nextTick();
  fillingEditor = false;
  editorDirty.value = false;
  await focusEditor();
}

/**
 * 能不能删，以及不能删的原因。
 *
 * 后端最终还会再判一次；这里提前算出来是为了让入口本身说话——
 * 试用反馈里「建错的阶段无法删除」，一部分原因就是按钮藏在抽屉底部、
 * 点了才知道不让删。现在光标悬停就能看到理由。
 */
/**
 * 删不掉的理由；空串表示可以删。
 *
 * 原来还有一条「旧系统同步的节点不能删」——那条挡住了删大阶段这件正常的事
 * （Tony 2026-08-18）。现在后端对旧节点做软删（界面上没了、数据留痕），
 * 前端不再拦；剩下两条是真的会出乱子：有下级会留下孤儿，有交付物会丢文件。
 */
function deleteBlockReason(task: PlanTask, hasChildren: boolean): string {
  if (hasChildren) {
    return task.nodeType === 'phase'
      ? '这个阶段下面还有子阶段或任务，先把它们删掉或在编辑里改挂到别的阶段下'
      : '下面还挂着任务，先删除或移走下级任务';
  }
  if (task.outputs?.length) return '这个节点挂着交付物，先处理交付物或设为「不适用」';
  return '';
}

async function deleteNode(task: PlanTask, hasChildren = false): Promise<void> {
  const blocked = deleteBlockReason(task, hasChildren);
  if (blocked) { notify('error', '不能删除', blocked); return; }
  const confirmed = await confirmAction({
    title: `确认删除「${task.name}」？`,
    message: task.isManual === true
      ? '这条是在 PDM 里建的，删了就没有了。'
      : '这条来自旧系统，删除后界面上不再出现，数据保留一份痕迹以备核对。',
    danger: true,
    confirmLabel: task.nodeType === 'phase' ? '删除阶段' : '删除节点',
  });
  if (!confirmed) return;
  saving.value = true;
  try {
    await eqsApi.deletePlanNode(props.project.id, task.id);
    notify('success', '节点已删除', '');
    if (editingId.value === task.id) {
      editorDirty.value = false;
      editorOpen.value = false;
    }
    await load();
  } catch (caught) {
    notify('error', '删除失败', errorMessage(caught));
  } finally {
    saving.value = false;
  }
}

/** 抽屉里的删除：拿当前编辑的那条 */
function deleteEditingNode(): void {
  const task = allTasks.value.find((item) => item.id === editingId.value);
  if (!task) return;
  const hasChildren = treeRows.value.some((row) => row.task.id === task.id && row.hasChildren);
  void deleteNode(task, hasChildren);
}

/** 新建：接口只收这几格，协办人 / 状态 / 完成度建完再进抽屉改 */
function createPayload(): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: editName.value.trim(),
    nodeType: editNodeType.value,
    applicable: editApplicable.value,
    applicabilityReason: editReason.value.trim(),
    // 姓名由后端按账号补写；没选人就不发，别把「未分配」写成一个空账号
    ...(editOwnerId.value === null ? {} : { ownerUserId: editOwnerId.value }),
    keyDeliverable: editKeyDeliverable.value,
    requiredReviewRoles: splitRoles(editReviewRoles.value),
    ...(parentNodeId.value ? { parentNodeId: parentNodeId.value } : {}),
  };
  const start = isoDate(editStart.value);
  const end = isoDate(editEnd.value, true);
  if (start) payload.plannedStartAt = start;
  if (end) payload.plannedEndAt = end;
  return payload;
}

/**
 * 编辑：只发和打开时不一样的字段（0930）。
 *
 * · 后端按字段分权限：只带状态 / 完成度 / 实际完成的，项目组成员就能存——
 *   责任人在抽屉里报进度不会再被当成「改计划」挡掉；
 * · 负责人没动就一个字都不发：无账号的文本责任人（旧数据、外部供应商）
 *   不会因为顺手改了个日期就被悄悄解绑；清掉账号才发 ownerUserId: null；
 * · 所属阶段改到顶层要显式发 null，undefined 会被后端当成「不改」。
 */
function editPatch(): Record<string, unknown> {
  const base = editorBase.value;
  if (!base) return {};
  const patch: Record<string, unknown> = {};
  const name = editName.value.trim();
  if (name !== base.name) patch.name = name;
  if (parentNodeId.value !== base.parentNodeId) patch.parentNodeId = parentNodeId.value;
  if (editStatus.value !== base.status) patch.status = editStatus.value;
  if (editApplicable.value !== base.applicable) patch.applicable = editApplicable.value;
  const reason = editReason.value.trim();
  if (reason !== base.reason) patch.applicabilityReason = reason;
  if (editOwnerId.value !== base.ownerId) patch.ownerUserId = editOwnerId.value;
  const helpers = sortedIds(editHelperIds.value, editOwnerId.value);
  if (!sameList(helpers, base.helperIds)) patch.helperUserIds = helpers;
  if (editStart.value !== base.start) patch.plannedStartAt = isoDate(editStart.value) ?? null;
  if (editEnd.value !== base.end) patch.plannedEndAt = isoDate(editEnd.value, true) ?? null;
  if (editActual.value !== base.actual) patch.actualAt = isoDate(editActual.value, true) ?? null;
  if (editProgress.value !== base.progress) patch.progress = editProgress.value;
  if (editKeyDeliverable.value !== base.keyDeliverable) patch.keyDeliverable = editKeyDeliverable.value;
  const roles = splitRoles(editReviewRoles.value);
  if (!sameList(roles, base.reviewRoles)) patch.requiredReviewRoles = roles;
  return patch;
}

/** 保存成功那句话：说清改了什么，别一律「负责人、适用性、日期和评审信息已保存」 */
const PATCH_LABEL: Readonly<Record<string, string>> = {
  name: '名称', parentNodeId: '所属阶段', status: '状态', applicable: '是否适用',
  applicabilityReason: '备注', ownerUserId: '负责人', helperUserIds: '协办人',
  plannedStartAt: '计划开始', plannedEndAt: '计划完成', actualAt: '实际完成', progress: '完成度',
  keyDeliverable: '需评审', requiredReviewRoles: '评审角色', completionNote: '完成说明',
};

async function saveNode(): Promise<void> {
  if (!editName.value.trim()) {
    notify('error', '无法保存', '请填写活动名称。');
    return;
  }
  const creating = editorMode.value === 'create';
  let payload: Record<string, unknown>;
  if (creating) {
    if (!await confirmAction({ title: '确认新增该计划节点？所有变更都会进入审计记录。' })) return;
    payload = createPayload();
  } else {
    if (editingId.value === null) return;
    payload = editPatch();
    // 什么都没改：直接关，不发一个空请求、不留一条空审计
    if (!Object.keys(payload).length) {
      editorDirty.value = false;
      editorOpen.value = false;
      return;
    }
    /*
      标完成要有证据（0930）：挂了交付物的每份都要有文件，没挂的要写完成说明。
      后端同一条判据还会再拦一次（plan-node-completion-gate.ts），这里是为了保存前就说清楚。
    */
    if (editBecomingDone.value) {
      if (editCompletionGate.value?.kind === 'files') await loadDeliverableStats();
      const gate = editCompletionGate.value;
      if (gate?.kind === 'files') {
        notify('error', '还不能标完成', missingFilesText(gate.missing));
        return;
      }
      if (gate?.kind === 'note') {
        if (!completionNoteEnough(editCompletionNote.value)) {
          notify('error', '还不能标完成', NOTE_REQUIRED_TEXT);
          completionNoteInput.value?.focus();
          return;
        }
        payload.completionNote = editCompletionNote.value.trim();
      }
    }
    // 抽屉里改了已排期活动的计划完成日 → 问原因（0926）
    const original = allTasks.value.find((item) => String(item.id) === String(editingId.value));
    const nextEndDay = editEnd.value || null;
    if ('plannedEndAt' in payload && original && nodeNeedsRescheduleReason(original, nextEndDay)) {
      const answer = await reschedule.ask({ title: original.name, fromDay: dayOf(original.planEnd), toDay: nextEndDay });
      if (!answer) return;
      Object.assign(payload, rescheduleFields(answer, 'single'));
    }
  }
  const action = creating ? '新增' : '更新';
  saving.value = true;
  try {
    const response = (creating
      ? await eqsApi.createPlanNode(props.project.id, payload)
      : await eqsApi.updatePlanNode(props.project.id, editingId.value!, payload)) as unknown as
      Record<string, unknown> | undefined;
    // 评审角色自动匹配的结果要说出来：谁进了评审链、哪个角色没配上
    const sync = response?.reviewerSync as {
      addedReviewers?: string[]; alreadyPresent?: number; unmatchedRoles?: string[];
      waitingForDeliverables?: string[]; pendingReviewers?: string[];
    } | null | undefined;
    // 没交东西评审人先压着不发——要说出来，否则保存的人以为配置丢了
    if (sync?.waitingForDeliverables?.length) {
      notify('success', '评审角色已保存，评审人先不通知',
        `「${sync.waitingForDeliverables.join('、')}」还没上传文件；`
        + `传上去后 ${sync.pendingReviewers?.join('、') ?? '相应评审人'} 会自动收到评审任务。`);
    }
    if (sync?.addedReviewers?.length) {
      notify('success', `已按角色加入 ${sync.addedReviewers.length} 位评审人`,
        `${sync.addedReviewers.join('、')} 会在「我的工作」看到待评审。`);
    }
    if (sync?.unmatchedRoles?.length) {
      notify('error', `角色「${sync.unmatchedRoles.join('、')}」没有匹配到人`,
        '项目团队里没有担任该角色的成员；请在「团队」页补充成员。');
    }
    if (creating) {
      notify('success', '计划节点已新增', '日期和负责人已保存，计划甘特里同步显示。');
    } else {
      const changed = Object.keys(payload).map((key) => PATCH_LABEL[key]).filter(Boolean);
      notify('success', '已保存', `改了${changed.join('、')}；计划甘特里同步显示。`);
    }
    editorDirty.value = false;
    editorOpen.value = false;
    await load();
    if (parentNodeId.value) expandAll();
  } catch (caught) {
    notify('error', `计划节点${action}失败`, errorMessage(caught));
  } finally {
    saving.value = false;
  }
}

/**
 * 本项目团队里出现过的角色。
 *
 * 「评审角色」保存后是按角色名去项目团队里找人的——名字对不上就静默派空，
 * 界面上没有任何提示，和团队页「项目角色」自由文本是同一个毛病（8/13 盘点）。
 * 这里把团队里真实存在的角色列出来点选，并对填了但队里没有的角色出声。
 */
const teamRoles = ref<string[]>([]);

async function loadTeamRoles(): Promise<void> {
  try {
    const payload = await eqsApi.projectResourceRecords('hr', 'project-members', props.project.id);
    const seen = new Set<string>();
    for (const member of payload.items ?? []) {
      const role = String(member.code ?? '').trim();
      if (role && String(member.status ?? 'active') !== 'inactive') seen.add(role);
    }
    teamRoles.value = [...seen].sort();
  } catch {
    teamRoles.value = [];
  }
}

/** 输入框里已经写了的角色 */
const enteredRoles = computed(() => editReviewRoles.value
  .split(/[；;]/u)
  .map((role) => role.trim())
  .filter(Boolean));

/** 填了、但本项目团队里没这个角色的人——保存后这一条评审链会是空的 */
const unmatchedRoles = computed(() => (teamRoles.value.length
  ? enteredRoles.value.filter((role) => !teamRoles.value.includes(role))
  : []));

function toggleReviewRole(role: string): void {
  const current = enteredRoles.value;
  const next = current.includes(role)
    ? current.filter((item) => item !== role)
    : [...current, role];
  editReviewRoles.value = next.join('；');
}

async function loadPeople(): Promise<void> {
  try {
    const payload = await eqsApi.people();
    people.value = (payload.people ?? []) as unknown as typeof people.value;
  } catch {
    // 取不到名册就退回只读显示，不能因为下拉挂了让整张计划表不能用
    people.value = [];
  }
}

function openDeliverables(task: PlanTask): void {
  void router.push({
    name: 'project-deliverables',
    params: { projectId: String(props.project.id) },
    query: {
      nodeId: String(task.id),
      taskName: task.name,
    },
  });
}

async function applyBulkUpdate(): Promise<void> {
  if (!selectedIds.value.length) return;
  const patch: Record<string, unknown> = {};
  if (bulkStatus.value) patch.status = bulkStatus.value;
  if (bulkApplicable.value) patch.applicable = bulkApplicable.value === 'true';
  // 批量也走账号：原来是个自由文本框，一批下去会把这些节点的账号全解掉，
  // 名字留在表上、待办谁也收不到
  if (bulkResponsibility.value) patch.ownerUserId = Number(bulkResponsibility.value);
  const start = isoDate(bulkStart.value);
  const end = isoDate(bulkEnd.value, true);
  if (start) patch.plannedStartAt = start;
  if (end) patch.plannedEndAt = end;
  if (!Object.keys(patch).length) {
    notify('error', '没有可更新内容', '请至少填写一项批量修改内容。');
    return;
  }
  // 改计划完成日要写原因（0926）：这批里只要有一条已排期、还开着的，就得写；批量一律按排期调整记
  if (end) {
    const needing = selectedIds.value
      .map((id) => allTasks.value.find((task) => String(task.id) === String(id)))
      .filter((task): task is PlanTask => task !== undefined && nodeNeedsRescheduleReason(task, bulkEnd.value));
    if (needing.length) {
      if (!bulkReason.value.trim()) {
        notify('error', '改期要写原因', `这批里有 ${needing.length} 条已排期的活动要改计划完成日期，请填「为什么改」。`);
        return;
      }
      Object.assign(patch, rescheduleFields({ category: bulkReasonCategory.value, reason: bulkReason.value.trim() }, 'batch'));
    }
  }
  /*
    批量标完成也过「完成要有证据」（0930）：后端逐条拦、一条不过整批不写，所以这里先说清楚——
    交付物没传完的点名出来整批不改；没挂交付物的要一句完成说明，记到那几条的改动记录上。
  */
  if (bulkStatus.value === 'completed') {
    const finishing = selectedIds.value
      .map((id) => allTasks.value.find((task) => String(task.id) === String(id)))
      .filter((task): task is PlanTask => task !== undefined && task.nodeType !== 'phase' && !isPlanNodeDone(task));
    const lacking = () => finishing.filter((task) => completionGateOf(task)?.kind === 'files');
    if (lacking().length) await loadDeliverableStats();
    const blocked = lacking();
    if (blocked.length) {
      notify('error', `${blocked.length} 条任务还不能标完成`,
        `交付物没传完：${blocked.slice(0, 5).map((task) => `「${task.name}」`).join('')}${blocked.length > 5 ? ' 等' : ''}。`
        + '传完文件再标，或者先把这几条从勾选里去掉。');
      return;
    }
    const needNote = finishing.filter((task) => completionGateOf(task)?.kind === 'note');
    if (needNote.length) {
      const note = await promptAction({
        title: `标完成 ${finishing.length} 条任务`,
        message: `其中 ${needNote.length} 条没挂交付物，要写一句完成说明：做了什么、结果放在哪。会记到这几条的改动记录上。`,
        prompt: '完成说明',
        promptPlaceholder: '例如：P03 评审会已开，纪要放在项目文件夹',
        promptRequired: true,
        confirmLabel: '继续',
      });
      if (note === null) return;
      if (!completionNoteEnough(note)) {
        notify('error', '没有标完成', NOTE_REQUIRED_TEXT);
        return;
      }
      patch.completionNote = note.trim();
    }
  }
  if (!await confirmAction({ title: `确认批量更新 ${selectedIds.value.length} 个计划节点？` })) return;
  bulkSaving.value = true;
  try {
    await eqsApi.batchUpdatePlanNodes(
      props.project.id,
      selectedIds.value.map((nodeId) => ({ nodeId, ...patch })),
    );
    notify('success', '批量修改完成', `已更新 ${selectedIds.value.length} 个节点。`);
    bulkOpen.value = false;
    bulkReason.value = '';
    selectedIds.value = [];
    await load();
  } catch (caught) {
    notify('error', '批量修改失败，未写入', errorMessage(caught));
  } finally {
    bulkSaving.value = false;
  }
}

async function previewImport(): Promise<void> {
  const file = importInput.value?.files?.[0];
  if (!file) {
    notify('error', '请选择文件', '请上传从本页面下载的 XLSX 更新模板。');
    return;
  }
  const form = new FormData();
  form.append('file', file);
  importLoading.value = true;
  importPreview.value = null;
  try {
    importPreview.value = await eqsApi.previewPlanImport(props.project.id, form);
    notify(
      importPreview.value.invalid ? 'error' : 'success',
      'Excel 校验完成',
      `变更 ${importPreview.value.changed} 行，异常 ${importPreview.value.invalid} 行；当前尚未写入。`,
    );
  } catch (caught) {
    notify('error', 'Excel 校验失败', errorMessage(caught));
  } finally {
    importLoading.value = false;
  }
}

async function confirmImport(): Promise<void> {
  if (!importPreview.value || importPreview.value.invalid) return;
  const updates = importPreview.value.rows
    .filter((row) => Object.keys(row.changes).length > 0)
    .map((row) => ({ nodeId: row.nodeId, ...row.changes }));
  const creations = (importPreview.value.creations ?? [])
    .filter((row) => !row.errors.length)
    .map((row) => row.payload);
  if (!updates.length && !creations.length) return;
  const summary = [
    updates.length ? `更新 ${updates.length} 行` : '',
    creations.length ? `新增 ${creations.length} 个节点` : '',
  ].filter(Boolean).join('、');
  if (!await confirmAction({ title: `确认写入 Excel 中的变更（${summary}）？` })) return;
  // Excel 里改了已排期活动的计划完成日同样要原因（0926）：一次问一遍，记到每一行上，来源 import
  const needsReason = updates.filter((update) => {
    if (!('plannedEndAt' in update)) return false;
    const task = allTasks.value.find((item) => String(item.id) === String(update.nodeId));
    return task !== undefined
      && nodeNeedsRescheduleReason(task, dayOf((update as Record<string, unknown>).plannedEndAt as string | null));
  });
  if (needsReason.length) {
    const answer = await reschedule.ask({ title: 'Excel 批量更新', fromDay: null, toDay: null, count: needsReason.length });
    if (!answer) return;
    for (const update of needsReason) Object.assign(update, rescheduleFields(answer, 'import'));
  }
  importConfirming.value = true;
  try {
    const result = await eqsApi.confirmPlanImport(props.project.id, updates, creations) as unknown as {
      created?: number;
      reviewerNotes?: Array<{ name: string; addedReviewers?: string[]; unmatchedRoles?: string[] }>;
    };
    notify('success', 'Excel 批量更新完成', `已${summary}。`);
    // 新增节点带评审角色的，把自动匹配结果说出来
    for (const note of result.reviewerNotes ?? []) {
      if (note.addedReviewers?.length) {
        notify('success', `「${note.name}」已按角色加入 ${note.addedReviewers.length} 位评审人`,
          note.addedReviewers.join('、'));
      }
      if (note.unmatchedRoles?.length) {
        notify('error', `「${note.name}」的角色「${note.unmatchedRoles.join('、')}」没匹配到人`,
          '项目团队里没人担任该角色，请在「团队」页补充成员。');
      }
    }
    importPreview.value = null;
    if (importInput.value) importInput.value.value = '';
    await load();
  } catch (caught) {
    notify('error', 'Excel 批量更新中断', errorMessage(caught));
    // 部分新增可能已落库（报错信息里带行号说明），刷新让人看到真实状态
    await load();
  } finally {
    importConfirming.value = false;
  }
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.projectPlan(props.project.id);
    if (!payload || !Array.isArray(payload.tasks)) throw new Error('项目计划响应缺少 tasks 数组');
    data.value = payload;
  } catch (caught) {
    data.value = null;
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
  // 表格受 loading 控制，必须先显示再定位，不能在加载占位仍在时找行。
  await nextTick();
  await focusRequestedTask();
}

async function focusRequestedTask(): Promise<void> {
  const rawNodeId = Array.isArray(route.query.nodeId) ? route.query.nodeId[0] : route.query.nodeId;
  if (!rawNodeId || !data.value || loading.value) return;
  const task = data.value.tasks.find((item) => String(item.id) === String(rawNodeId));
  if (!task) return;
  gapFilter.value = null;
  noticeFilter.value = { mine: false, kind: null };
  const stage = phaseFor(task) || 'all';
  if (stage && phaseFilter.value !== stage) {
    phaseFilter.value = stage;
    await nextTick();
  }
  const ancestors: string[] = [];
  const seen = new Set<string>();
  let current: PlanTask | undefined = task;
  while (current?.parentId !== null && current?.parentId !== undefined) {
    const parent = String(current.parentId);
    if (!parent || parent === '0' || seen.has(parent)) break;
    seen.add(parent);
    ancestors.push(parent);
    current = activityMap.value.get(parent);
  }
  expandedIds.value = [...new Set([...(stage ? [stage] : []), ...ancestors])];
  selectedIds.value = [task.id];
  await nextTick();
  document.querySelector(`[data-plan-node-id="${String(task.id)}"]`)
    ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  const editRequested = (Array.isArray(route.query.edit) ? route.query.edit[0] : route.query.edit) === '1';
  /*
    打开抽屉不看写权限：评审面板、进展留痕、输出物都在里面，
    评审人从「我的工作」的「去评审」点进来必须能打开（0915 回归）。
    抽屉里能不能改由各控件自己判。
  */
  if (editRequested) await openEdit(task);
  /*
    从「我的工作」的「去完成」进来的（0930）：工作台上不再一键完成，点进来直接把完成度拉到 100，
    抽屉里马上看得到还差什么——缺哪几份文件，或者要写一句完成说明。保存还是人自己点。
  */
  const focusComplete = (Array.isArray(route.query.focus) ? route.query.focus[0] : route.query.focus) === 'complete';
  if (editRequested && focusComplete && canReport.value && task.nodeType !== 'phase' && !isPlanNodeDone(task)) {
    applyProgressEdit({ field: 'progress', value: 100 });
    await nextTick();
    document.querySelector('.ne-evidence')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }
  /*
   * 从「去评审」进来的：抽屉是一整页编辑表单，评审面板沉在最底部，
   * 人落进来根本不知道评审在哪（试用反馈 8/13 截图）。
   * 滚到评审面板并高亮几秒——告诉人「你要做的事在这里」。
   */
  const focusReview = (Array.isArray(route.query.focus) ? route.query.focus[0] : route.query.focus) === 'review';
  if (focusReview) {
    reviewFocus.value = true;
    await nextTick();
    window.setTimeout(() => {
      const panel = document.querySelector('.review-panel');
      panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      panel?.classList.add('review-panel-spotlight');
      window.setTimeout(() => panel?.classList.remove('review-panel-spotlight'), 3500);
    }, 250);
  }
}

watch(() => [route.query.nodeId, route.query.edit, route.query.focus], () => {
  void focusRequestedTask();
});
watch(() => [route.query.mine, route.query.overdue, route.query.duesoon], applyNoticeFilterFromQuery);

onMounted(() => {
  void loadDeliverableStats();
  applyNoticeFilterFromQuery();
  void loadTeamRoles();
  void load();
  void loadPeople();
  void loadFolders();
});
</script>

<template>
  <!--
    验收点（1027 复评 r2）：桌面 1440×900、默认态（「概况」收起），首屏能看到阶段表（原约 1045px 才露头）。
    说明条还在老位置，它就是「概况」：说明 · 维护统计 ·「找出看着已经做完的节点」收在里面，点开原样都在；
    阶段筛选、展开 / 收起、新增、批量修改留在原处；Excel、模板、铺设计划、导出、打包收进工具条的「更多操作」。
    不缩正文字号。条放在加载占位外面：确认完成后重新加载，展开着的「概况」不会自己合上。
  -->
  <!-- 用 div 不用 header：里面「找完成」那块自带 <header>，header 里不能再套 header -->
  <div class="subpage-context ap-context">
    <details v-if="data" class="ap-digest">
      <summary>
        <span class="ap-digest-tag">ACTIVITY PLAN</span>
        <span>{{ data.tasks.length }} 个节点 · 数据截至 {{ formatDate(data.asOf) }}</span>
        <em>说明 · 维护统计{{ canWrite ? ' · 找出已做完的节点' : '' }}</em>
      </summary>
      <p class="ap-digest-note">
        集中维护各阶段活动、责任人、适用性、日期、输出物和评审要求。
        完整阶段树集中在本页；可单条维护、批量更新或用 Excel 集中调整。快速补排期请进入“计划甘特”。
      </p>
      <ActivityPlanDashboard :tasks="allTasks" @drill="gapFilter = $event" />

      <!-- 系统提醒、人确认：一条都不自动标完成 -->
      <section v-if="canWrite" class="ap-completion">
        <header>
          <button type="button" class="button" :disabled="completionBusy" @click="askCompletion">
            {{ completionBusy ? '正在找…' : completion ? '重新找一遍' : '找出「看着已经做完」的节点' }}
          </button>
          <button
            v-if="openCompletion.length" type="button" class="button button-primary"
            :disabled="completionBusy" @click="confirmCompletion(openCompletion)"
          >全部确认（{{ openCompletion.length }}）</button>
          <small v-if="completion">
            {{ completion.pendingNodes }} 个节点没填完成日期，其中
            <b>{{ completion.suggestions.length }}</b> 个有「已经做完」的痕迹。
            剩下的系统找不到依据，只能靠人填。
          </small>
          <small v-else>
            按交付物是否批准 / 是否挂了文件、资料目录里同名文件夹有没有文件、旧 EQS 的状态来找。
            找到了也<strong>不会自动标完成</strong>，要你点确认。
          </small>
        </header>

        <ul v-if="completionOpen && openCompletion.length">
          <li v-for="item in openCompletion" :key="item.nodeId">
            <div>
              <b>{{ item.nodeName }}</b>
              <span>
                建议完成日期 <strong>{{ item.suggestedDate }}</strong>
                <template v-if="item.ownerName"> · 负责人 {{ item.ownerName }}</template>
              </span>
              <em>{{ item.reasons.join('；') }}</em>
            </div>
            <div class="ap-completion-act">
              <button type="button" class="button button-primary" :disabled="completionBusy" @click="confirmCompletion([item])">确认完成</button>
              <button type="button" class="inline-button" @click="completionDone = new Set(completionDone).add(item.nodeId)">先不填</button>
            </div>
          </li>
        </ul>
      </section>
    </details>
    <p v-else><span>ACTIVITY PLAN</span>集中维护各阶段活动、责任人、适用性、日期、输出物和评审要求。</p>
  </div>
  <PageState v-if="loading" state="loading" title="正在读取项目计划" message="加载阶段、活动、责任人和日期。" />
  <PageState v-else-if="error" state="error" title="项目计划不可用" message="不会用静态甘特或推算日期替代。" :detail="error" @retry="load" />
  <template v-else-if="data">
    <!--
      改不了的时候要说清为什么（0914 第 12 条）。
      按钮灰掉却不给理由，人只会以为系统坏了——0829 的教训:
      「藏起来又挡路是最坏的组合」。
    -->
    <p v-if="planReadOnly" class="plan-readonly">{{ planReadOnly }}</p>
    <p v-if="gapFilter" class="ap-drill">
      看板筛选生效中
      <button type="button" @click="gapFilter = null">清除</button>
    </p>
    <!-- 从通知点进来时明确说清楚为什么只剩这几行，否则看着像数据丢了 -->
    <p v-if="noticeFilterLabel" class="ap-drill ap-notice-drill">
      从通知进入：只显示「{{ noticeFilterLabel }}」的 {{ visibleTasks.length }} 项
      <button type="button" @click="clearNoticeFilter">显示全部</button>
    </p>

    <section class="plan-filter" aria-label="计划筛选">
      <label>
        <span>阶段</span>
        <select v-model="phaseFilter">
          <option value="all">全部阶段</option>
          <option v-for="phase in phaseOptions" :key="phase.value" :value="phase.value">{{ phase.label }}</option>
        </select>
      </label>
      <strong>{{ visibleTasks.length }} <small>/ {{ data.tasks.length }} 节点</small></strong>
    </section>

    <section class="content-panel activity-plan-panel">
      <header class="section-heading activity-heading">
        <div>
          <span>ACTIVITY PLAN</span>
          <h2>阶段工作内容</h2>
        </div>
        <strong>{{ treeRows.length }} 已显示</strong>
      </header>

      <div class="plan-toolbar" role="toolbar" aria-label="阶段工作内容操作">
        <div class="toolbar-group">
          <button class="button button-secondary" type="button" @click="expandAll">全部展开</button>
          <button class="button button-secondary" type="button" @click="collapseAll">全部收起</button>
          <button class="button button-primary" type="button" :disabled="!canWrite" @click="openCreate()">新增子阶段</button>
          <button
            class="button button-secondary"
            type="button"
            :disabled="!canWrite || !selectedIds.length"
            @click="bulkOpen = !bulkOpen"
          >批量修改（{{ selectedIds.length }}）</button>
          <PlanOwnerAssignmentDialog v-if="canWrite" :project="project" @done="load" />
        </div>
        <!--
          导入导出这一拨收进「更多操作」（1027 复评 r2）：两组并排要折成两行，把表往下顶。
          收起时内容仍在 DOM 里，选好的更新文件不会因为收起就丢。
        -->
        <details class="toolbar-more">
          <summary class="button button-secondary">更多操作</summary>
          <div class="toolbar-group">
            <label v-if="canWrite" class="file-pick">
              <span>更新文件</span>
              <input ref="importInput" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" @change="importPreview = null">
            </label>
            <button v-if="canWrite" class="button button-secondary" type="button" :disabled="importLoading" @click="previewImport">
              {{ importLoading ? '正在校验…' : 'Excel 批量更新' }}
            </button>
            <a class="button button-secondary" :href="eqsApi.planWorkbookUrl(project.id)">下载模板</a>
            <!--
              「重新铺设计划」放在这一组：它是整页级的动作，和上面的单条维护不同量级。
              替代「删了重建」——项目号是沿用的，人想推倒重来原来只能新建一个项目，
              而那会让 BOM、文件、变更、团队全留在旧项目行上（0916 的 DH26005）。
              权限和改排期同一档（canWrite 就是 useProjectPlanWrite）。
            -->
            <PlanRelayDialog v-if="canWrite" :project="project" @done="load" />
            <ExportDataButton
              :columns="planExportColumns"
              :rows="exportRows"
              :filename="exportFilename"
              label="导出当前阶段"
            />
            <button class="button button-secondary" type="button" @click="downloadAttachments()">打包全部附件</button>
          </div>
        </details>
      </div>

      <section v-if="bulkOpen" class="batch-panel" aria-label="批量修改">
        <label><span>状态</span><select v-model="bulkStatus"><option value="">不修改</option><option value="pending">待处理</option><option value="in_progress">进行中</option><option value="blocked">阻塞</option><option value="completed">完成</option><option value="not_applicable">不适用</option></select></label>
        <label><span>是否适用</span><select v-model="bulkApplicable"><option value="">不修改</option><option value="true">适用</option><option value="false">不适用</option></select></label>
        <label>
          <span>负责人</span>
          <select v-model="bulkResponsibility">
            <option value="">不修改</option>
            <option v-for="person in people" :key="person.id" :value="String(person.id)">
              {{ person.displayName }}{{ person.orgName ? `（${person.orgName}）` : '' }}
            </option>
          </select>
        </label>
        <label><span>计划开始</span><input v-model="bulkStart" type="date"></label>
        <label><span>计划完成</span><input v-model="bulkEnd" type="date"></label>
        <!-- 已排期的活动改计划完成日要写原因（0926）；填了日期才出这两格 -->
        <label v-if="bulkEnd">
          <span>改期原因分类</span>
          <select v-model="bulkReasonCategory" aria-label="改期原因分类">
            <option v-for="entry in DELAY_CATEGORIES" :key="entry" :value="entry">{{ entry }}</option>
          </select>
        </label>
        <label v-if="bulkEnd" class="batch-reason">
          <span>为什么改</span>
          <input v-model="bulkReason" type="text" maxlength="500" aria-label="批量改期原因" placeholder="已排期的活动改日期必须写原因，按排期调整记">
        </label>
        <button class="button button-primary" type="button" :disabled="bulkSaving" @click="applyBulkUpdate">
          {{ bulkSaving ? '正在保存…' : `确认修改 ${selectedIds.length} 项` }}
        </button>
      </section>

      <section v-if="importPreview" class="import-preview" :class="{ 'has-error': importPreview.invalid }">
        <div>
          <b>Excel 校验结果</b>
          <p>
            共 {{ importPreview.total }} 行 · 变更 {{ importPreview.changed }}
            · 新增 {{ importPreview.creations?.length ?? 0 }}
            · 未变 {{ importPreview.unchanged }} · 异常 {{ importPreview.invalid }}（尚未写入）
          </p>
        </div>
        <ul v-if="importPreview.creations?.length" class="creation-list">
          <li v-for="row in importPreview.creations.slice(0, 10)" :key="`c-${row.row}`">
            新增{{ row.nodeType === 'phase' ? '阶段' : '任务' }}「{{ row.name }}」→ {{ row.parentLabel }}
          </li>
          <li v-if="importPreview.creations.length > 10">…共 {{ importPreview.creations.length }} 个新增节点</li>
        </ul>
        <ul v-if="importPreview.invalid">
          <li
            v-for="row in [...importPreview.rows, ...(importPreview.creations ?? [])].filter((item) => item.errors.length).slice(0, 8)"
            :key="`e-${row.row}`"
          >
            第 {{ row.row }} 行 {{ row.name || '（未命名）' }}：{{ row.errors.join('；') }}
          </li>
        </ul>
        <button
          v-if="importPreview.changed || importPreview.creations?.length"
          class="button button-primary"
          type="button"
          :disabled="Boolean(importPreview.invalid) || importConfirming"
          @click="confirmImport"
        >{{ importConfirming ? '正在写入…' : importPreview.invalid ? '请先修正异常行' : `确认写入（更新 ${importPreview.changed} · 新增 ${importPreview.createValid ?? 0}）` }}</button>
        <p v-else class="no-change">文件内容与系统当前计划一致，无需写入。</p>
      </section>

      <div class="data-table-wrap activity-table-wrap">
        <table class="data-table activity-table">
          <thead>
            <tr>
              <th><input type="checkbox" aria-label="选择当前显示节点" @change="toggleAllVisible"></th>
              <th>活动</th>
              <th>类型</th>
              <th title="勾选后该节点的交付物需要走审批流">需评审</th>
              <th>输出物</th>
              <th title="模板给定的建议岗位，不等于实际指派的人">参考角色</th>
              <th>适用</th>
              <th title="从系统人员里选；选中即绑定账号，这个人才收得到待办">负责人</th>
              <th>计划开始</th>
              <th>计划完成</th>
              <th>实际完成</th>
              <th title="0–100；填 100 算做完（实际完成记今天）。阶段按下面的活动自动汇总，和计划甘特同一个数">完成度</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <!-- 整行可点（鼠标）：原来只有最右边那个「编辑」两个字能点，
                 一行十二列要横向拖到最右侧才够得着。
                 刻意不给行加 tabindex——229 行就是 229 个多余的 Tab 停靠点，
                 键盘用户仍走行内那个已经带 aria-label 的「编辑」按钮。 -->
            <tr
              v-for="row in treeRows"
              :key="row.task.id"
              :data-plan-node-id="row.task.id"
              class="activity-row"
              :class="{ 'phase-row': row.task.nodeType === 'phase', 'is-selected': rowSelected(row.task) }"
              @click="openEditFromRow($event, row.task)"
            >
              <td><input type="checkbox" :checked="rowSelected(row.task)" :aria-label="`选择${row.task.name}`" @change="toggleSelection(row.task)"></td>
              <td class="activity-name">
                <div :style="{ paddingLeft: `${row.depth * 22}px` }">
                  <button
                    v-if="row.hasChildren"
                    class="tree-toggle"
                    type="button"
                    :aria-label="`${isExpanded(row.task) ? '收起' : '展开'}${row.task.name}`"
                    @click="toggleExpanded(row.task)"
                  >{{ isExpanded(row.task) ? '▾' : '▸' }}</button>
                  <span v-else class="tree-spacer" aria-hidden="true"></span>
                  <b>{{ row.task.name }}</b>
                </div>
              </td>
              <td>{{ row.task.nodeType === 'phase' ? '阶段' : '任务' }}</td>
              <td>
                <button
                  class="cell-switch"
                  type="button"
                  :class="[{ off: !row.task.keyDeliverable }, cellStatus(row.task, 'review')]"
                  :disabled="!canWrite"
                  :aria-label="`${row.task.keyDeliverable ? '取消' : '设为'}需评审：${row.task.name}`"
                  @click="onReviewToggle(row.task)"
                >{{ row.task.keyDeliverable ? '是' : '否' }}</button>
              </td>
              <td class="output-cell">
                <span v-if="row.task.outputs?.length">{{ row.task.outputs.join('；') }}</span>
                <span v-else>—</span>
                <small v-if="row.task.reviewRoles?.length">评审：{{ row.task.reviewRoles.join('、') }}</small>
                <!-- 实际交付情况：不加第 16 列，挂在应交清单下面（0819 反馈） -->
                <RouterLink
                  v-if="deliverableStatOf(row.task)"
                  class="output-deliverables"
                  :to="deliverableLink(row.task)"
                  @click.stop
                >交付物 {{ deliverableStatOf(row.task)!.withDoc }}/{{ deliverableStatOf(row.task)!.total }} 已交<template v-if="deliverableStatOf(row.task)!.approved"> · {{ deliverableStatOf(row.task)!.approved }} 已批准</template> →</RouterLink>
              </td>
              <td class="role-cell">{{ row.task.ownerRole || '—' }}</td>
              <td>
                <button
                  class="cell-switch"
                  type="button"
                  :class="[{ off: row.task.applicable === false }, cellStatus(row.task, 'applicable')]"
                  :disabled="!canWrite"
                  :aria-label="`${row.task.applicable === false ? '启用' : '停用'}：${row.task.name}`"
                  @click="onApplicableToggle(row.task)"
                >{{ row.task.applicable === false ? '否' : '是' }}</button>
              </td>
              <td class="owner-cell">
                <PersonPicker
                  v-if="canWrite && people.length"
                  :people="people"
                  :model-value="row.task.ownerUserId ?? null"
                  :legacy-name="row.task.ownerUserId ? null : row.task.ownerName"
                  :label="`${row.task.name} 的负责人`"
                  list-id="plan-people-options"
                  :tone="cellStatus(row.task, 'owner')"
                  @update:model-value="onOwnerPicked(row.task, $event)"
                  @unresolved="onOwnerUnresolved"
                />
                <span v-else>{{ formatResponsibility(row.task.ownerName || row.task.ownerRole, '未分配') }}</span>
                <!-- 系统按团队角色落的负责人要标出来（0929）：不标的话人会以为已经有人排过，不再确认 -->
                <small
                  v-if="row.task.ownerSource === 'team-role' && row.task.ownerUserId"
                  class="owner-auto"
                  title="团队里这个角色只有他，系统按角色自动落的；团队换人会跟着换。在这里改成别人或清空后，就不再自动改。"
                >按团队角色</small>
                <small v-if="row.task.helpers?.length" class="helper-line">协办 {{ row.task.helpers.map((helper) => helper.name).join('、') }}</small>
              </td>
              <td v-for="field in (['planStart', 'planEnd', 'actualEnd'] as const)" :key="field" class="date-cell">
                <!-- 计划开始/完成是排期，归项目经理；实际完成是「我干完了」，责任人自己填 -->
                <input
                  v-if="field === 'actualEnd' ? canReport : canWrite"
                  class="cell-input"
                  :class="cellStatus(row.task, field)"
                  type="date"
                  :value="row.task[field]?.slice(0, 10) || ''"
                  :aria-label="`${row.task.name} 的${field === 'planStart' ? '计划开始' : field === 'planEnd' ? '计划完成' : '实际完成'}`"
                  @change="onDateChange(row.task, field, $event)"
                >
                <span v-else>{{ formatDate(row.task[field]) }}</span>
              </td>
              <td class="progress-cell">
                <!--
                  完成度同样是报进度，不是改计划。阶段行按下面的活动汇总、做完的就是 100，
                  这两种只显示不给改（数从 utils/plan-progress 来，和甘特上那条同一个）。
                -->
                <input
                  v-if="progressEditableInline(row.task)"
                  class="cell-input"
                  :class="cellStatus(row.task, 'progress')"
                  type="number"
                  min="0"
                  max="100"
                  step="5"
                  :value="row.task.progress ?? ''"
                  :aria-label="`${row.task.name} 的完成度百分比`"
                  @change="onProgressChange(row.task, $event)"
                >
                <span
                  v-else
                  class="progress-read"
                  :class="{ rollup: progressOf(row.task).source === 'rollup' }"
                  :title="progressTitle(progressOf(row.task))"
                >{{ progressText(progressOf(row.task)) }}</span>
                <i v-if="progressOf(row.task).value !== null" class="progress-meter" aria-hidden="true">
                  <b :style="{ width: `${progressOf(row.task).value}%` }"></b>
                </i>
              </td>
              <td><StatusChip :value="planChipValue(row.task)" :label="planStatusLabel(row.task)" /></td>
              <td class="row-actions">
                <button class="inline-button" type="button" :disabled="!canWrite" :aria-label="`编辑${row.task.name}`" @click="openEdit(row.task)">编辑</button>
                <button class="inline-button" type="button" :disabled="!canWrite" :aria-label="`在${row.task.name}下新增任务`" @click="openCreate(row.task, 'task')">+任务</button>
                <button class="inline-button deliverable-link" type="button" :aria-label="`管理${row.task.name}的交付物和文件`" @click="openDeliverables(row.task)">交付物 / 文件</button>
                <!-- 旧 EQS 任务看板上的三个按钮：资料文件夹 / 导出附件 / 邮件提醒 -->
                <button
                  v-if="folderFor(row.task)"
                  class="inline-button folder-link"
                  type="button"
                  :title="folderFor(row.task)?.source === 'deliverable'
                    ? `交付物已归入「${folderFor(row.task)?.title ?? '阶段目录'}」，本任务交了 ${folderFor(row.task)?.fileCount ?? 0} 个文件`
                    : `${folderFor(row.task)?.title ?? '资料文件夹'}（${folderFor(row.task)?.fileCount ?? 0} 个文件）`"
                  :aria-label="`打开${row.task.name}的资料文件夹`"
                  @click="openFolder(row.task)"
                ><Folder :size="18" color="currentColor" aria-hidden="true" /> {{ folderFor(row.task)?.fileCount ?? 0 }}</button>
                <button
                  class="inline-button"
                  type="button"
                  :aria-label="`打包下载${row.task.name}的附件`"
                  @click="downloadAttachments(row.task)"
                >导出附件</button>
                <button
                  class="inline-button"
                  type="button"
                  :disabled="!canWrite || reminding === row.task.id"
                  :aria-label="`催办${row.task.name}的责任人`"
                  @click="remind(row.task)"
                >{{ reminding === row.task.id ? '发送中…' : '催办' }}</button>
                <!-- 手工建错的节点在行上就能删；旧系统同步的不显示这个入口，
                     免得点开才被拒（试用反馈：建错的阶段找不到删除） -->
                <button
                  v-if="canWrite && row.task.isManual"
                  class="inline-button inline-button-danger"
                  type="button"
                  :disabled="Boolean(deleteBlockReason(row.task, row.hasChildren))"
                  :title="deleteBlockReason(row.task, row.hasChildren) || `删除${row.task.name}`"
                  :aria-label="`删除${row.task.name}`"
                  @click="deleteNode(row.task, row.hasChildren)"
                >删除</button>
              </td>
            </tr>
            <tr v-if="!treeRows.length">
              <td class="empty-detail" colspan="14">当前阶段没有计划节点。</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- 全表共用一份人员名单：每行各渲染一份的话，200 行 × 109 人 = 两万个节点 -->
      <datalist id="plan-people-options">
        <option v-for="person in people" :key="person.id" :value="personLabel(person)" />
      </datalist>
    </section>

    <!-- 编辑挪进右侧抽屉：这张表 12 个字段，原来的内联面板挤在表格下面铺不开，
         而且离被编辑的那一行很远，改到第 200 行时根本对不上号。 -->
    <NodeRescheduleDialog
      v-if="reschedule.pending.value"
      :title="reschedule.pending.value.title"
      :from-day="reschedule.pending.value.fromDay"
      :to-day="reschedule.pending.value.toDay"
      :count="reschedule.pending.value.count"
      @confirm="reschedule.settle($event)"
      @cancel="reschedule.settle(null)"
    />
    <AppDrawer
      v-if="editorOpen"
      :kicker="editorMode === 'create' ? 'NODE / CREATE' : 'NODE / EDIT'"
      :title="editorMode === 'create'
        ? (editNodeType === 'phase' ? '新增子阶段' : '新增任务')
        : (editingTask?.name || editName || '编辑计划节点')"
      :dirty="editorDirty"
      @close="editorOpen = false"
    >
      <!-- 名称挪进了「更多设置」，标题上得写明改的是哪一条 -->
      <template v-if="editorMode === 'edit' && editingTask" #subtitle>
        <p class="ne-subtitle">
          {{ editingTask.nodeType === 'phase' ? '阶段' : '任务' }}
          · 负责人 {{ formatResponsibility(editingTask.ownerName || editingTask.ownerRole, '未分配') }}
          · {{ PLAN_STATUS_LABEL[editingTask.status ?? ''] ?? editingTask.status }}
        </p>
      </template>
      <div ref="editorPanel" class="node-editor-body">
        <!--
          0930 抽屉正面只留排期和进度：计划开始 / 完成、完成度、实际完成、状态、负责人。
          其余收进「更多设置」。谁能改哪一格照旧按字段分（0915）：
          排期、负责人和「更多设置」归项目经理；完成度、实际完成、状态是责任人对自己那条活的陈述，
          项目组成员都能报——原来非项目经理打开是一张只读卡片，要报进度还得关掉抽屉回表格里找那一格。
        -->
        <form class="node-form" @submit.prevent="saveNode">
          <p v-if="editorMode === 'edit' && !canWrite" class="ne-note">
            {{ canReport
              ? '排期和负责人归项目经理改。完成度、实际完成、状态是你自己那条活的进度，在这里直接报。'
              : planReadOnly }}
          </p>

          <template v-if="editorMode === 'create'">
            <label class="ne-field"><span>活动名称</span><input v-model="editName" type="text" maxlength="200" aria-label="活动名称"></label>
            <label class="ne-field">
              <span>所属阶段 <b class="field-hint">挂在哪个大阶段下面；留空就是顶层阶段</b></span>
              <select v-model="parentNodeId" aria-label="所属阶段">
                <option :value="null">（顶层阶段）</option>
                <option v-for="phase in parentOptions" :key="phase.id" :value="Number(phase.id)">{{ phase.name }}</option>
              </select>
            </label>
          </template>

          <div class="ne-pair">
            <label class="ne-field">
              <span>计划开始</span>
              <input v-model="editStart" type="date" aria-label="计划开始" :disabled="editorMode === 'edit' && !canWrite">
            </label>
            <label class="ne-field">
              <span>计划完成</span>
              <input v-model="editEnd" type="date" aria-label="计划完成" :disabled="editorMode === 'edit' && !canWrite">
            </label>
          </div>

          <section v-if="editorMode === 'edit'" class="ne-progress" aria-label="完成度">
            <header>
              <span>完成度</span>
              <b>{{ editIsRollup ? progressText(progressOf(editingTask)) : (editProgress === null ? '未填' : `${editProgress}%`) }}</b>
            </header>
            <!-- 阶段不手填：按下面的活动算，和甘特上阶段那条同一个数 -->
            <template v-if="editIsRollup">
              <div class="ne-meter" aria-hidden="true"><i :style="{ width: `${progressOf(editingTask).value ?? 0}%` }"></i></div>
              <small class="ne-help">{{ progressTitle(progressOf(editingTask)) }}。阶段的完成度由下面的活动自动算，改下面的活动就行。</small>
            </template>
            <template v-else>
              <input
                class="ne-range"
                type="range"
                min="0"
                max="100"
                step="5"
                :value="editProgress ?? 0"
                :disabled="!canReport"
                aria-label="拖动调整完成度"
                @input="applyProgressEdit({ field: 'progress', value: Number(($event.target as HTMLInputElement).value) })"
              >
              <div class="ne-quick">
                <button
                  v-for="step in [0, 25, 50, 75, 100]"
                  :key="step"
                  type="button"
                  class="ne-chip"
                  :class="{ on: editProgress === step }"
                  :disabled="!canReport"
                  @click="applyProgressEdit({ field: 'progress', value: step })"
                >{{ step }}%</button>
                <label class="ne-number">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    placeholder="未填"
                    :value="editProgress ?? ''"
                    :disabled="!canReport"
                    aria-label="完成度百分比"
                    @change="onDraftProgressText"
                  ><span>%</span>
                </label>
              </div>
              <small class="ne-help">拉到 100% 就算做完（带上实际完成日期）；甘特条按这个比例上色。</small>
            </template>
          </section>

          <div v-if="editorMode === 'edit'" class="ne-pair">
            <label class="ne-field">
              <span>实际完成 <b class="field-hint">填了就算做完</b></span>
              <input
                :value="editActual"
                type="date"
                :max="shanghaiToday()"
                aria-label="实际完成"
                :disabled="!canReport"
                @change="applyProgressEdit({ field: 'actual', value: ($event.target as HTMLInputElement).value })"
              >
            </label>
            <label class="ne-field">
              <span>状态</span>
              <select
                :value="editStatus"
                aria-label="状态"
                :disabled="!canReport"
                @change="applyProgressEdit({ field: 'status', value: ($event.target as HTMLSelectElement).value })"
              >
                <option v-for="status in editStatusOptions" :key="status" :value="status">
                  {{ PLAN_STATUS_LABEL[status] ?? status }}
                </option>
              </select>
            </label>
          </div>
          <p v-if="progressLinkHint" class="ne-link-hint">{{ progressLinkHint }}</p>
          <!--
            标完成要有证据（0930 Tony：「工作台不能直接点击完成…偷懒直接完成了」）。
            只在这一笔要把任务变成「做完」时出现：挂了交付物的列出还缺哪几份文件，
            没挂交付物的要写一句完成说明。后端同一条判据还会再拦一次。
          -->
          <section v-if="editCompletionGate" class="ne-evidence" :class="`is-${editCompletionGate.kind}`" aria-label="完成依据">
            <template v-if="editCompletionGate.kind === 'files'">
              <b>还不能标完成：{{ editCompletionGate.missing.length }} 份交付物没传文件</b>
              <ul>
                <li v-for="(title, index) in editCompletionGate.missing.slice(0, 6)" :key="index">{{ title }}</li>
                <li v-if="editCompletionGate.missing.length > 6">……还有 {{ editCompletionGate.missing.length - 6 }} 份</li>
              </ul>
              <small>传完才能标完成；不需要的交付物请项目经理在交付物页取消。</small>
              <RouterLink v-if="editingTask" class="button button-secondary" :to="deliverableLink(editingTask)">去交付物页传文件 →</RouterLink>
            </template>
            <label v-else-if="editCompletionGate.kind === 'note'" class="ne-field">
              <span>完成说明 <b class="field-hint">这条任务没挂交付物：写一句做了什么、结果放在哪（至少 {{ COMPLETION_NOTE_MIN_CHARS }} 个字），会记进改动记录</b></span>
              <textarea
                ref="completionNoteInput"
                v-model="editCompletionNote"
                rows="2"
                maxlength="500"
                placeholder="例如：评审会已开，纪要放在项目文件夹 P03"
                aria-label="完成说明"
              ></textarea>
            </label>
            <small v-else>交付物 {{ editCompletionGate.live }} 份都已传文件，可以标完成。</small>
          </section>

          <label class="ne-field">
            <span>负责人 <b v-if="editorMode === 'create' || canWrite" class="field-hint">直接输姓名，下拉会跟着筛</b></span>
            <PersonPicker
              v-if="editorMode === 'create' || canWrite"
              :people="people"
              :model-value="editOwnerId"
              :legacy-name="editOwnerLegacyName || null"
              label="负责人"
              list-id="plan-people-options"
              @update:model-value="editOwnerId = $event; editOwnerLegacyName = ''"
              @unresolved="onOwnerUnresolved"
            />
            <span v-else class="ne-static">{{ formatResponsibility(editingTask?.ownerName || editingTask?.ownerRole, '未分配') }}</span>
          </label>

          <!--
            不常用、而且多半在别处已经有入口的几格：适用、需评审在表格行上能直接点，
            审批人在交付物那边配。默认收起；数据一格没删，改了照样保存。
          -->
          <details class="ne-more" :open="moreOpen" @toggle="moreOpen = ($event.target as HTMLDetailsElement).open">
            <summary>
              更多设置
              <small>{{ editorMode === 'edit' ? '名称、所属阶段、适用性、协办人、评审角色' : '适用性、评审角色' }}——平时不用动</small>
            </summary>
            <div class="ne-more-body">
              <template v-if="editorMode === 'edit'">
                <label class="ne-field"><span>活动名称</span><input v-model="editName" type="text" maxlength="200" aria-label="活动名称" :disabled="!canWrite"></label>
                <label class="ne-field">
                  <span>所属阶段 <b class="field-hint">挂在哪个大阶段下面；留空就是顶层阶段</b></span>
                  <select v-model="parentNodeId" aria-label="所属阶段" :disabled="!canWrite">
                    <option :value="null">（顶层阶段）</option>
                    <option v-for="phase in parentOptions" :key="phase.id" :value="Number(phase.id)">{{ phase.name }}</option>
                  </select>
                </label>
              </template>
              <div class="ne-pair">
                <label class="ne-field">
                  <span>是否适用</span>
                  <select v-model="editApplicable" aria-label="是否适用" :disabled="editorMode === 'edit' && !canWrite">
                    <option :value="true">适用</option><option :value="false">不适用</option>
                  </select>
                </label>
                <label class="ne-field">
                  <span title="勾选后该节点的交付物需要走审批流；和表格上「需评审」是同一个开关">需评审（关键交付物）</span>
                  <select v-model="editKeyDeliverable" aria-label="需评审" :disabled="editorMode === 'edit' && !canWrite">
                    <option :value="false">否</option><option :value="true">是</option>
                  </select>
                </label>
              </div>
              <label class="ne-field">
                <span>不适用原因 / 备注</span>
                <textarea v-model="editReason" rows="2" maxlength="500" :disabled="editorMode === 'edit' && !canWrite"></textarea>
              </label>
              <label v-if="editorMode === 'edit'" class="ne-field">
                <span>协办人 <b class="field-hint">零件多、两个人交叉处理时用。协办人也能在「我的工作」里看到并改状态；负荷和逾期只算负责人</b></span>
                <PeoplePicker v-if="canWrite" v-model="editHelperIds" :multiple="true" placeholder="搜索姓名 / 工号，可选多人" />
                <span v-else class="ne-static">{{ editingTask?.helpers?.map((helper) => helper.name).join('、') || '无' }}</span>
              </label>
              <label class="ne-field">
                <span>评审角色 <b class="field-hint">交付物没挂审批流时，按这里的角色在项目团队里找审批人</b></span>
                <input
                  v-model="editReviewRoles"
                  type="text"
                  placeholder="点下面的角色添加，或手工输入、多个用；分隔"
                  :disabled="editorMode === 'edit' && !canWrite"
                >
                <span v-if="teamRoles.length && (editorMode === 'create' || canWrite)" class="role-chips">
                  <button
                    v-for="role in teamRoles"
                    :key="role"
                    type="button"
                    class="role-chip"
                    :class="{ on: enteredRoles.includes(role) }"
                    @click="toggleReviewRole(role)"
                  >{{ role }}</button>
                </span>
                <small v-else-if="!teamRoles.length" class="field-error">本项目团队还没有成员，填了角色也派不到人——先到「团队」页加人。</small>
                <small v-if="unmatchedRoles.length" class="field-error">
                  团队里没有这些角色的人，按角色找审批人会找空：{{ unmatchedRoles.join('、') }}
                </small>
              </label>
              <button
                v-if="editorMode === 'edit' && canWrite && editingTask?.isManual"
                class="button button-danger" type="button" :disabled="saving"
                @click="deleteEditingNode"
              >删除此节点</button>
            </div>
          </details>

          <div v-if="editorMode === 'create' || canWrite || canReport" class="ne-actions">
            <button class="button button-primary" type="submit" :disabled="saving">
              {{ saving ? '正在保存…' : editorMode === 'create' ? '创建计划节点' : '保存' }}
            </button>
            <small v-if="editorMode === 'edit'">只保存改过的格子，计划甘特里同步显示。</small>
          </div>
        </form>

        <template v-if="editorMode === 'edit' && editingId !== null">
          <!--
            0911 Tony:「阶段工作与项目交付物的审批流不一致；建议阶段工作内取消审批界面」。
            审批是交付物的事，这一页管排期和进度。留一句话指路——直接删光的话，
            人会以为审批功能没了，转头去别处找（0829 的教训：藏起来又挡路是最坏的组合）。
          -->
          <p class="node-route-hint">
            交付物和审批在「项目交付物」页。
            <RouterLink v-if="editingTask" :to="deliverableLink(editingTask)">
              <template v-if="deliverableStatOf(editingTask)">
                交付物 {{ deliverableStatOf(editingTask)!.withDoc }}/{{ deliverableStatOf(editingTask)!.total }} 已交<template v-if="deliverableStatOf(editingTask)!.approved"> · {{ deliverableStatOf(editingTask)!.approved }} 已批准</template>
              </template>
              <template v-else>看这个节点的交付物</template> →
            </RouterLink>
          </p>
          <!--
            指名评审（0916 停发、存量收尾）：名单上还有人的节点才显示。
            线上 0930 还有 160 个节点 245 行没出结论、两周里出了 344 个结论，这块不能撤；
            但其余 3900 多个节点一行都没有，每次打开都顶着一段「正在收尾」只是噪音。
          -->
          <NodeReviewPanel
            v-show="reviewFocus || reviewPanelState === 'rows' || reviewPanelState === 'failed'"
            :project-id="project.id"
            :node-id="editingId"
            :can-write="canReport"
            :people="people"
            @settled="load"
            @loaded="reviewPanelState = $event === null ? 'failed' : $event > 0 ? 'rows' : 'empty'"
          />
          <!-- 改动记录：进展留痕（状态和日期）+ 字段级审计，点开才读 -->
          <details class="ne-history" :open="historyOpen" @toggle="historyOpen = ($event.target as HTMLDetailsElement).open">
            <summary>改动记录<small>谁在什么时候改了日期、状态、负责人……排期有争议时对账用</small></summary>
            <template v-if="historyOpen">
              <NodeProgressHistory :project-id="project.id" :node-id="editingId" />
              <RecordHistory
                entity-type="plan-node"
                :entity-id="editingId"
                hint="状态和日期之外的改动（责任人、名称、是否适用等）都在这里。"
              />
            </template>
          </details>
        </template>
      </div>
    </AppDrawer>
  </template>
</template>

<style scoped>
.owner-auto { display: inline-block; margin-top: 3px; padding: 0 6px; border: 1px solid #cfe0f5; border-radius: 3px; background: #f3f8fe; color: #2b5ea8; font-size: 11px; line-height: 1.7; }
.plan-readonly {
  margin: 0 0 14px; padding: 10px 14px;
  border: 1px solid var(--line); border-left: 3px solid var(--blue);
  border-radius: var(--radius-s); background: var(--white);
  color: var(--muted); font-size: 12px; line-height: 1.8;
}

/* 完成提示：系统给依据，人点确认 */
.ap-completion {
  margin: 0 0 16px; padding: 12px 14px;
  border: 1px solid var(--line); border-left: 3px solid var(--blue);
  border-radius: var(--radius-s); background: var(--white);
}
.ap-completion header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ap-completion header small { flex: 1 1 280px; color: var(--muted); font-size: 11px; line-height: 1.7; }
.ap-completion ul {
  display: grid; gap: 6px; margin: 12px 0 0; padding: 0; list-style: none;
  max-height: 320px; overflow: auto;
}
.ap-completion li {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 9px 12px; border: 1px solid var(--line); border-radius: var(--radius-s);
  background: var(--paper);
}
.ap-completion li > div:first-child { flex: 1 1 320px; display: grid; gap: 2px; min-width: 0; }
.ap-completion b { font-size: 13px; }
.ap-completion span { color: var(--muted); font-size: 12px; }
.ap-completion strong { color: var(--blue); }
.ap-completion em { font-style: normal; color: var(--muted); font-size: 11px; line-height: 1.6; }
.ap-completion-act { display: flex; align-items: center; gap: 10px; }

/*
  「概况」（1027 复评 r2）：说明条本身就是可展开的摘要，收起时和原来的说明条一样高；
  展开后是原来的说明、维护统计和「找出看着已经做完的节点」。字号照原样，不缩。
*/
.ap-context > .ap-digest { flex: 1 1 auto; align-self: stretch; min-width: 0; }
.ap-digest > summary {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-height: 36px;
  color: var(--muted); font-size: 13px; line-height: 1.5; cursor: pointer;
}
.ap-digest-tag { color: var(--blue); font: 800 13px var(--display); letter-spacing: .12em; }
.ap-digest > summary em { margin-left: auto; color: var(--blue); font-style: normal; font-size: 12px; white-space: nowrap; }
.ap-digest > summary em::after { content: ' ▾'; }
.ap-digest[open] > summary em::after { content: ' ▴'; }
.ap-digest-note { margin: 4px 0 0; color: var(--muted); font-size: 13px; line-height: 1.8; }
.ap-digest .ap-completion { margin: 0 0 6px; }

/* 「更多操作」：收起时就是工具条末尾一个按钮；展开后另起一行，整组导入导出原样排开 */
.toolbar-more > summary { list-style: none; color: var(--blue); }
.toolbar-more > summary::-webkit-details-marker { display: none; }
.toolbar-more > summary::after { content: '▾'; margin-left: 6px; }
.toolbar-more[open] { flex-basis: 100%; }
.toolbar-more[open] > summary::after { content: '▴'; }
.toolbar-more > .toolbar-group { margin-top: 10px; }
/* ---------- 节点抽屉（0930 精简：正面只留排期和进度） ---------- */
/* 壳（遮罩 / 动画 / 头尾）由 AppDrawer 统一提供 */
.node-editor-body { display: grid; gap: 16px; align-content: start; padding: 16px 22px 28px; }
.ne-subtitle { margin: 0; color: var(--muted); font-size: 12px; }
.node-form { display: grid; gap: 14px; }
.ne-note {
  margin: 0; padding: 8px 12px; border-left: 3px solid var(--blue);
  background: #EEF4FA; color: var(--ink); font-size: 12px; line-height: 1.7;
}
.ne-pair { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.ne-field { display: grid; gap: 5px; min-width: 0; }
.ne-field > span:first-child { color: var(--muted); font-size: 12px; }
.ne-field input,
.ne-field select,
.ne-field textarea {
  width: 100%; min-height: 38px; padding: 0 10px;
  border: 1px solid var(--line); border-radius: var(--radius-s);
  color: var(--ink); background: var(--white); font: inherit; font-size: 14px;
}
.ne-field textarea { min-height: 64px; padding: 8px 10px; resize: vertical; }
/* 改不了的格子照样读得清：只换底色，不把字弄灰 */
.ne-field input:disabled,
.ne-field select:disabled,
.ne-field textarea:disabled { background: var(--paper); color: var(--ink); cursor: not-allowed; opacity: 1; }
.ne-static { min-height: 38px; display: flex; align-items: center; font-size: 14px; }

.ne-progress {
  display: grid; gap: 10px; padding: 14px 16px;
  border: 1px solid #CFE0F5; border-radius: var(--radius-s); background: #F5F9FE;
}
.ne-progress header { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.ne-progress header span { color: var(--muted); font-size: 12px; }
.ne-progress header b { color: var(--blue); font: 800 24px var(--display); }
.ne-range { width: 100%; margin: 0; accent-color: var(--blue); }
.ne-quick { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.ne-chip {
  min-width: 52px; min-height: 30px; padding: 0 10px;
  border: 1px solid var(--line); border-radius: 999px;
  background: var(--white); color: var(--ink); font: 700 12px var(--body); cursor: pointer;
}
.ne-chip:hover:not(:disabled) { border-color: var(--blue); color: var(--blue); }
.ne-chip.on { border-color: var(--blue); background: var(--blue); color: var(--white); }
.ne-chip:disabled { cursor: not-allowed; opacity: .55; }
.ne-number { display: inline-flex; align-items: center; gap: 4px; margin-left: auto; color: var(--muted); font-size: 12px; }
.ne-number input {
  width: 72px; min-height: 30px; padding: 0 8px;
  border: 1px solid var(--line); border-radius: var(--radius-s);
  background: var(--white); text-align: right; font: inherit; font-size: 13px;
}
.ne-meter { height: 8px; border-radius: 4px; background: #DCE7F4; overflow: hidden; }
.ne-meter i { display: block; height: 100%; background: var(--blue); }
.ne-help { color: var(--muted); font-size: 12px; line-height: 1.6; }
.ne-link-hint { margin: -4px 0 0; color: var(--warning); font-size: 12px; line-height: 1.6; }
/* 标完成要有证据（0930）：缺文件红框挡住，要写说明黄框提醒，齐了绿字一句 */
.ne-evidence {
  display: grid; gap: 8px; padding: 12px 14px;
  border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white);
}
.ne-evidence.is-files { border-color: #F0C2C2; background: #FDF5F5; }
.ne-evidence.is-files > b { color: var(--risk); font-size: 13px; }
.ne-evidence.is-files ul { margin: 0; padding-left: 20px; font-size: 13px; line-height: 1.7; }
.ne-evidence.is-files small { color: var(--muted); font-size: 12px; }
.ne-evidence.is-files .button { justify-self: start; }
.ne-evidence.is-note { border-color: #F0DDB0; background: #FFFBF1; }
.ne-evidence.is-ok small { color: var(--green); font-size: 12px; }

.ne-more,
.ne-history { border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); }
/*
  summary 一旦设成 flex，浏览器自带的三角就没了（PFMEA 页踩过）：
  关掉原生标记，自己画一个，开合都看得出来。
*/
.ne-more > summary,
.ne-history > summary {
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
  padding: 10px 14px; list-style: none; cursor: pointer; font-size: 13px; font-weight: 700;
}
.ne-more > summary::-webkit-details-marker,
.ne-history > summary::-webkit-details-marker { display: none; }
.ne-more > summary::before,
.ne-history > summary::before { content: '▸'; color: var(--muted); font-size: 12px; }
.ne-more[open] > summary::before,
.ne-history[open] > summary::before { content: '▾'; }
.ne-more > summary small,
.ne-history > summary small { color: var(--muted); font-size: 12px; font-weight: 400; }
.ne-more[open] > summary,
.ne-history[open] > summary { border-bottom: 1px solid var(--line); }
.ne-more-body { display: grid; gap: 12px; padding: 12px 14px 14px; }
.ne-history > :not(summary) { margin: 10px 14px 14px; }
.ne-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ne-actions small { color: var(--muted); font-size: 12px; }

.node-route-hint {
  margin: 0; padding-top: 12px; border-top: 1px solid var(--line);
  color: var(--muted); font-size: 12px; line-height: 1.7;
}
.node-route-hint a { margin-left: 4px; color: var(--blue); font-weight: 700; }

/* 「去评审」落点高亮；scoped 影响不到子组件根节点，用 deep */
:deep(.review-panel-spotlight) {
  outline: 3px solid var(--blue); outline-offset: 3px; border-radius: 4px;
  transition: outline-color 1.2s ease;
}
.ap-notice-drill { border-left: 3px solid var(--blue); }
.role-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.role-chip {
  padding: 3px 9px; border: 1px solid var(--line); border-radius: 999px;
  background: var(--white); color: var(--muted); font-size: 12px; cursor: pointer;
}
.role-chip:hover { border-color: var(--blue); color: var(--blue); }
.role-chip.on { border-color: var(--blue); background: rgba(0, 117, 174, .1); color: var(--blue); font-weight: 700; }
.field-hint { color: var(--muted); font-size: 12px; font-weight: 400; }
.owner-filter { margin-bottom: 6px; }
/* 整行可点，给出对应的光标与悬停反馈 */
.activity-row { cursor: pointer; }
.activity-row:hover td { background: #F4F8FC; }

.ap-drill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  padding: 4px 10px;
  background: #EEF4FA;
  color: var(--blue);
  font-size: 12px;
}

.ap-drill button { min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap; color: var(--blue); }
.ap-drill button:hover { border-color: var(--blue); background: rgba(0,117,174,.07); }

/* 1027 复评 r2：阶段筛选压成一行——标签和下拉并排、边距收紧，字号不动 */
.plan-filter {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-top: 12px;
  padding: 8px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
}

.plan-filter label {
  min-width: min(320px, 70vw);
  display: flex;
  align-items: center;
  gap: 10px;
}

.plan-filter label span,
.plan-filter strong small {
  color: var(--muted);
  font-size: 12px;
}

.plan-filter select {
  min-height: 42px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
  font-size: 14px;
}

.plan-filter select option { color: var(--ink); }
.plan-filter strong { font: 700 24px var(--display); }

.activity-plan-panel { margin-top: 12px; }
.activity-heading { align-items: end; }

.plan-toolbar {
  display: flex;
  flex-wrap: wrap;
  /* 组内 10、组间 22：靠间距分出「看/改」和「导入导出」两拨，不用画分隔线，
     换行时也不会留下一根孤零零的竖线 */
  gap: 10px 22px;
  align-items: center;
  padding: 14px 0;
  border-top: 1px solid var(--line);
}

.toolbar-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

/* 与同排按钮同高（.button 是 38，窄屏 42），否则原生文件框会把整行顶歪 */
.file-pick {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: var(--muted);
  font-size: 12px;
}

.file-pick > span { white-space: nowrap; font-weight: 700; }

.file-pick input {
  max-width: 210px;
  padding: 7px 9px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  background: var(--white);
  font: inherit;
  font-size: 12px;
}

@media (max-width: 900px) {
  .file-pick { min-height: 42px; }
}

.batch-panel,
.import-preview {
  display: grid;
  grid-template-columns: repeat(5, minmax(130px, 1fr)) auto;
  gap: 10px;
  align-items: end;
  margin-bottom: 12px;
  padding: 14px;
  border: 1px solid #BFD6EE;
  background: #F5FAFF;
}

.batch-panel label {
  display: grid;
  gap: 5px;
  font-size: 12px;
}

.batch-panel input,
.batch-panel select {
  min-height: 38px;
  padding: 0 8px;
  border: 1px solid var(--line);
  background: white;
}

.import-preview {
  grid-template-columns: minmax(0, 1fr) auto;
}

.import-preview.has-error {
  border-color: #F0B9B2;
  background: #FFF8F6;
}

.import-preview p { margin: 3px 0 0; color: var(--muted); }
.import-preview ul { grid-column: 1 / -1; margin: 0; padding-left: 20px; color: var(--risk); }
.no-change { grid-column: 1 / -1; }

.activity-table-wrap { max-height: min(66vh, 720px); }
.activity-table { min-width: 1540px; }
.activity-table thead th { position: sticky; top: 0; z-index: 2; }
.activity-table th:first-child { width: 44px; }
.activity-table th:nth-child(2) { min-width: 310px; }
.activity-table th:nth-child(5) { min-width: 200px; }
.activity-table th:last-child { min-width: 142px; }

/* 阶段行做成整行更重的一条，和缩进的任务行拉开层级——
   旧 EQS 用文件夹图标做这件事，这里用底色 + 左侧竖条，缩放时不会糊 */
.activity-table tbody tr.phase-row { background: #EDF3F9; }
.activity-table tbody tr.phase-row td:first-child { box-shadow: inset 3px 0 0 var(--blue); }
.activity-table tbody tr.phase-row b { color: var(--ink); font-weight: 800; font-size: 14px; }

.activity-name > div {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 30px;
}

.tree-toggle,
.tree-spacer {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
}

.tree-toggle {
  border: 1px solid #B9C8D8;
  color: #235D92;
  background: white;
  cursor: pointer;
  font-size: 17px;
  line-height: 20px;
}

.output-deliverables { display: block; margin-top: 2px; color: var(--blue); font-size: 12px; text-decoration: none; }
.output-deliverables:hover { text-decoration: underline; }
.output-cell { color: var(--muted); }
.output-cell small { display: block; margin-top: 4px; color: #7A4E00; }

.role-cell { color: var(--muted); font-size: 12px; }
.progress-cell .cell-input { min-width: 72px; text-align: right; }
/* 汇总 / 已完成的完成度只显示；下面一道细条和甘特上的颜色同一个意思 */
.progress-read { display: block; padding: 4px 6px; font-size: 12px; text-align: right; }
.progress-read.rollup { color: var(--blue); font-weight: 700; }
.progress-meter { display: block; height: 3px; margin: 2px 6px 0; border-radius: 2px; background: #DCE7F4; overflow: hidden; }
.progress-meter b { display: block; height: 100%; background: var(--blue); }
.folder-link { color: #7A4E00; }

.cell-switch {
  min-width: 44px;
  height: 28px;
  border: 0;
  border-radius: 14px;
  color: white;
  background: #159B6B;
  cursor: pointer;
  font-weight: 800;
}

.cell-switch.off { background: #7E8996; }
.cell-switch:disabled { cursor: not-allowed; opacity: .6; }

/* 行内编辑：平时看着像纯文本，鼠标进去才显出边框，
   不然一屏几百个输入框，整张表会花得没法读 */
.cell-input {
  width: 100%;
  min-width: 128px;
  padding: 4px 6px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 12px;
}

.cell-input:hover { border-color: #C7D5E2; background: var(--white); }
.cell-input:focus { border-color: var(--blue); background: var(--white); outline: none; }
.owner-cell .cell-input { min-width: 148px; }

/* 存盘反馈只落在改动的那一格上；成功那下会自己退回去 */
.cell-input.saving, .cell-switch.saving { opacity: .55; }
.cell-input.saved { border-color: var(--green); background: #EEF9F3; }
.cell-switch.saved { box-shadow: 0 0 0 2px #A9E2C6; }
.cell-input.error { border-color: var(--risk); background: #FDF0F0; }
.cell-switch.error { box-shadow: 0 0 0 2px var(--risk); }

.row-actions {
  display: flex;
  gap: 6px;
  white-space: nowrap;
}

.row-actions .inline-button { min-width: 54px; }
.empty-detail { height: 78px; color: var(--muted); text-align: center; }

@media (max-width: 1000px) {
  .batch-panel { grid-template-columns: repeat(2, minmax(150px, 1fr)); }
}

@media (max-width: 600px) {
  .section-heading,
  .activity-heading,
  .import-preview {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .plan-filter label { min-width: 0; }
  .batch-panel { grid-template-columns: 1fr; }
  .ne-pair { grid-template-columns: 1fr; }
  .file-pick { width: 100%; flex-wrap: wrap; padding: 8px 10px; }
}
</style>
